import { acquisitionConsent } from "./analytics/acquisition";
import { getTurnstileToken, takeReadyTurnstileToken, TURNSTILE_HEADER } from "@/lib/turnstile";
import type { DriverRosterClearance } from "./driver-roster-types";
import type { BookingDriverOverview, HostScheduleDisplay } from "@workspace/booking-rules";
import { RateLimitError, parseRetryAfter, buildRateLimitMessage } from "@/lib/api-error";
import {
  fetchWithTransientRetry,
  getRecentNetworkFailureSummary,
} from "@/lib/transient-fetch";
import { isValidIsoDate } from "@/lib/utils";
import * as Sentry from "@sentry/react";
// Re-exported for back-compat / co-location with `BookingApiError`. A
// 429 from any bookings-api call throws the shared `RateLimitError`
// (same as users-api), which is suppressed in Sentry's `beforeSend`.
export { RateLimitError } from "@/lib/api-error";

import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "@/lib/auth-refresh";
import type { DeliveryAirport } from "@/lib/api";
import { getBookingAnalyticsContext } from "@/lib/analytics";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function getImpersonationHeaders(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem("bac_impersonation");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data.expiresAt < Date.now()) return {};
    if (!data.userId || !data.sessionId) return {};
    return {
      "X-Impersonate-User-Id": data.userId,
      "X-Impersonation-Session-Id": data.sessionId,
    };
  } catch {
    return {};
  }
}

function authHeaders(token: string, impersonationHeaders = getImpersonationHeaders()): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...impersonationHeaders,
  };
}

// Step-up gated calls (Task #3066) throw the shared ApiError so the
// UI can branch on `status === 401` / `body.error === "step_up_cancelled"`.
export class ApiError extends Error {
  status: number;
  body?: Record<string, unknown>;
  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export class BookingApiError extends Error {
  status: number;
  code: string | null;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "BookingApiError";
    this.status = status;
    this.body = body;
    this.code = typeof body?.code === "string" ? body.code : null;
  }
}

/**
 * The transient-read retry window spans up to 20s during deploy recovery, so
 * authenticated booking requests get a slightly larger whole-flight budget.
 * This bounds a black-holed fetch without cutting off the final recovery
 * attempt that fetchWithTransientRetry deliberately allows.
 */
export const BOOKING_API_FETCH_TIMEOUT_MS = 30_000;
async function authFetch<T>(path: string, token: string, init?: RequestInit, impersonationHeaders?: Record<string, string>): Promise<T> {
  const internalController = new AbortController();
  let timedOut = false;
  let rejectTimeout!: (reason: BookingApiTimeoutError) => void;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    rejectTimeout = reject;
  });
  const timer = setTimeout(() => {
    timedOut = true;
    internalController.abort();
    const classification = classifyBookingApiTimeout(path);
    reportBookingApiStall(path, classification);
    rejectTimeout(new BookingApiTimeoutError(classification));
  }, BOOKING_API_FETCH_TIMEOUT_MS);
  const onCallerAbort = () => internalController.abort();
  init?.signal?.addEventListener("abort", onCallerAbort);
  if (init?.signal?.aborted) onCallerAbort();

  const request = (requestToken: string) =>
    fetchWithTransientRetry(`${API_BASE}${path}`, {
      ...init,
      headers: { ...authHeaders(requestToken, impersonationHeaders), ...init?.headers },
      signal: internalController.signal,
    });

  const operation = (async (): Promise<T> => {
    // Task #5799 — the helper only retries idempotent (GET) reads; the
    // booking/charge/hold writes that go through authFetch carry a mutating
    // method and are issued exactly once (never retried).
    const res = await request(token);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) {
      // Mirror users-api: a 429 is a handled, non-fatal rate-limit
      // condition (a hammered user / the dashboard's parallel load
      // burst tripping the per-IP limiter), not a bug. Throw the shared
      // `RateLimitError` with a friendly, seconds-aware message instead
      // of a generic `BookingApiError` whose empty-body fallthrough was
      // the bare "API error 429" spamming Sentry (DRIVESHARE_V2-7E).
      const retryAfter = parseRetryAfter(res, body);
        throw new RateLimitError(buildRateLimitMessage(body, retryAfter), retryAfter);
      }
      if (res.status === 401) {
      // The token may have expired mid-flight (common on the dashboard,
      // which fires ~12 parallel reads on load). Attempt one silent
      // refresh before giving up. `getSharedRefreshedToken` coalesces all
      // concurrent 401 callers — including those from users-api,
      // listings-api and subscriptions-api — onto a single
      // `refreshSession` call so the dashboard never fires a burst of
      // concurrent refreshes that invalidate each other's refresh tokens.
        const freshToken = await getSharedRefreshedToken();
        if (freshToken && freshToken !== token) {
        // Retry once with the refreshed token. If this also fails, fall
        // through to the normal BookingApiError path below.
          const retryRes = await request(freshToken);
          if (retryRes.ok) return retryRes.json();
          const retryBody = await retryRes.json().catch(() => ({}));
          if (retryRes.status === 401 || retryBody.error === "session_expired") {
            forceSessionExpiredRedirect();
          }
          const retryMsg =
            (typeof retryBody?.message === "string" && retryBody.message) ||
            (typeof retryBody?.error === "string" && retryBody.error) ||
            `API error ${retryRes.status}`;
          throw new BookingApiError(retryMsg, retryRes.status, retryBody);
        }
        // No new token — session is truly dead.
        forceSessionExpiredRedirect();
        // Fall through so callers don't receive undefined.
      }
    // Prefer the human-readable `message` the server attaches next to its
    // machine `error` code — that's what the cancel route, refund engine,
    // and other failure paths use to explain WHY (e.g.
    // "missing_cancellation_policy" → "This booking has no recorded
    // cancellation policy. Contact support so we can backfill the booking
    // before cancelling."). The raw `error` slug is still preserved on
    // BookingApiError.body for callers that need to branch on it.
      const msg = (typeof body?.message === "string" && body.message)
        || (typeof body?.error === "string" && body.error)
        || `API error ${res.status}`;
      throw new BookingApiError(msg, res.status, body);
    }
    return await res.json();
  })();

  try {
    return await Promise.race([operation, timeoutPromise]);
  } catch (err) {
    if (err instanceof BookingApiTimeoutError) throw err;
    if (timedOut && (err as { name?: string } | null)?.name === "AbortError") {
      throw new BookingApiTimeoutError();
    }
    throw err;
  } finally {
    clearTimeout(timer);
    init?.signal?.removeEventListener("abort", onCallerAbort);
  }
}

export class ClaimApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ClaimApiError";
    this.code = code;
    this.status = status;
  }
}

async function claimRequest(claimToken: string, token: string, method: "GET" | "POST"): Promise<ClaimInviteResponse> {
  const path = `/bookings/claim/${encodeURIComponent(claimToken)}`;
  const res = await boundedBookingFetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(token),
  }, path);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ClaimApiError(body.error || `API error ${res.status}`, body.code || "UNKNOWN", res.status);
  }
  return body as ClaimInviteResponse;
}

export class QuoteApiError extends Error {
  code?: string;
  maxRadius?: number;
  /**
   * HTTP status of the failed quote response. Lets callers treat an expected
   * client-validation rejection (400 — e.g. `Invalid date format`, missing
   * required fields) as a handled inline error instead of capturing it as a
   * Sentry exception, while still reporting genuinely unexpected failures
   * (5xx, network).
   */
  status?: number;
  constructor(message: string, code?: string, maxRadius?: number, status?: number) {
    super(message);
    this.name = "QuoteApiError";
    this.code = code;
    this.maxRadius = maxRadius;
    this.status = status;
  }
}

/**
 * Task #8483 — a renter with a CAD-priced, partial-payment, delivery quote
 * (live FX lookup + live sales-tax estimate on every quote request) hit a
 * checkout that spun on "Updating price…" forever across five separate
 * attempts: `checkout_intents` showed every attempt reaching
 * `checkout_step_reached='payment'` with zero conversions and no Sentry
 * error — the signature of a silent hang, not a thrown error. The server
 * side of that chain is now bounded (see `fx-rates.ts` / `us-stripe-tax.ts`),
 * but the client fetch itself had no bound either: a stalled connection
 * (dropped packet, proxy black hole) left `apiFetch` awaiting forever,
 * which is exactly what left `updatingQuote` stuck `true`.
 *
 * `QuoteTimeoutError` is thrown ONLY when apiFetch's own internal watchdog
 * fires — never for a caller-driven `AbortSignal` cancellation (those keep
 * the standard `AbortError` name so existing "the caller doesn't want this
 * anymore, do nothing" handling is unaffected). Callers use this distinct
 * `code` to show a specific "still not answering — try again" message
 * instead of the generic could-not-load-pricing fallback.
 */
export const QUOTE_FETCH_TIMEOUT_MS = 15_000;

export class QuoteTimeoutError extends QuoteApiError {
  constructor() {
    super("The price request is taking longer than expected. Please try again.", "QUOTE_TIMEOUT", undefined, 408);
    this.name = "QuoteTimeoutError";
  }
}

/**
 * Task #8493 — the Cat Walker incident (#8483) went unnoticed for ~14 hours
 * because hitting this exact bound never reported anywhere; the UI recovers
 * (a retryable "quote stalled" state) but ops had no visibility. Best-effort
 * only: a reporting failure must never mask — or delay — the real
 * `QuoteTimeoutError` the caller is about to throw/has thrown.
 */
function reportQuoteStall(context: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({
      category: "quote-stall",
      level: "warning",
      message: "QUOTE_TIMEOUT",
      data: context,
    });
    Sentry.captureMessage("[quote-stall] client QUOTE_TIMEOUT hit", {
      level: "warning",
      tags: { code: "QUOTE_TIMEOUT" },
      extra: context,
    });
  } catch {
    // Reporting must never mask the real timeout error.
  }
}

async function apiFetch<T>(path: string, token?: string | null, turnstileToken?: string | null): Promise<T> {
  const startedAt = Date.now();
  // Task #1006: forward the renter's bearer token when present so backend
  // routes that opportunistically read the authenticated user (e.g. the
  // /bookings/quote verification precheck) can populate per-renter facts
  // like phone_verified and the stored phone number. Anonymous callers
  // still work — the server treats a missing/invalid token as "no user".
  const headers: Record<string, string> = token
    ? { ...(authHeaders(token) as Record<string, string>) }
    : { "Content-Type": "application/json" };
  // Task #3228 — attach the Turnstile token when configured.
  if (turnstileToken) headers[TURNSTILE_HEADER] = turnstileToken;
  // Task #8483 — bound the whole request (including transient retries)
  // with an internal watchdog so a stalled connection can never leave a
  // caller's loading flag stuck forever. Mirrors the pattern already used
  // for the feature-flags fetch (task #6110).
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), QUOTE_FETCH_TIMEOUT_MS);
  try {
    // Task #5799 — apiFetch only serves idempotent GET reads (the booking
    // widget quote, insurance-package pricing). Retry a transient 5xx /
    // network blip a few times before throwing so a momentary backend blip
    // stops surfacing as a user-facing error card and a Sentry QuoteApiError.
    const res = await fetchWithTransientRetry(`${API_BASE}${path}`, { headers, signal: controller.signal });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new QuoteApiError(body.error || `API error ${res.status}`, body.code, body.maxRadius, res.status);
    }
    return await res.json();
  } catch (err) {
    // Only OUR OWN watchdog throws QuoteTimeoutError. A caller doesn't pass
    // its own signal into apiFetch today, so any AbortError observed here
    // is unambiguously ours.
    if ((err as { name?: string } | null)?.name === "AbortError") {
      reportQuoteStall({
        route: path.split("?")[0],
        stage: "quote_request",
        elapsedMs: Date.now() - startedAt,
        budgetMs: QUOTE_FETCH_TIMEOUT_MS,
      });
      throw new QuoteTimeoutError();
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export interface CancellationPolicyDetails {
  type: string;
  label: string;
  description: string;
  rules: string[];
  discountPercent: number;
  surchargePercent?: number;
}

// Task #4316 — renter-facing view of a host-built quote (GET /bookings/quotes/:token).
export interface TripQuoteDetail {
  token: string;
  status: "sent" | "paid" | "expired" | "cancelled";
  conversationId: number | null;
  carId: number;
  rentalType: string;
  dateFrom: string;
  dateTo: string;
  pickupAt: string | null;
  dropoffAt: string | null;
  hours: number | null;
  isDelivery: boolean | null;
  deliveryDistance: number | null;
  airportCode: string | null;
  deliveryAddress: string | null;
  pickupLatitude: number | null;
  pickupLongitude: number | null;
  rentalFeeOverride: number;
  deliveryFeeOverride: number | null;
  cancellationPolicy: string | null;
  currencyCode: string | null;
  expiresAt: string;
  quoteSnapshot: PriceQuote | null;
  tripId: number | null;
}

export interface PriceQuote {
  pickupProof?: string;
  carId: number;
  rentalType: string;
  dateFrom: string;
  dateTo: string;
  numDays: number;
  baseRate: number;
  rentalFee: number;
  baseRentalFee: number;
  serviceFee: number;
  insuranceFee: number;
  insuranceDailyRate: number;
  insurancePackageId: number | null;
  insurancePackageName: string | null;
  /**
   * Task #2264 — populated for the synthetic NY Crum policy applied to
   * NY-located US self-drive cars. Carries the per-day liability/APD split,
   * limits, deductible and the stated value the APD price was based on
   * (clamped to the $150k coverage cap when the car's insured value is
   * higher). Null on every other booking — the standard tiered packages
   * keep using `insurancePackageId` + `insurancePackageName`.
   */
  insurancePolicy: InsurancePolicySnapshot | null;
  cancellationPolicy: string;
  refundableSurcharge: number;
  refundableSurchargePercent: number;
  policyTotals: {
    nonRefundable: { paymentDue: number; platformPaymentDue: number };
    flexible: { paymentDue: number; platformPaymentDue: number };
  };
  cancellationPolicyDetails: CancellationPolicyDetails;
  deliveryFee: number;
  deposit: number;
  couponDiscount: number;
  appliedCoupon: { id: number; code: string; discountType: string; discountValue: number } | null;
  // Task #8163 — Pro Owner booking discount applied by the server at quote
  // time (10% Standard/Classic, 15% Premier off the rental fee). 0 / null
  // when the renter isn't entitled.
  proOwnerBookingDiscount?: number;
  proOwnerTier?: string | null;
  paymentDue: number;
  ownerEarn: number;
  /**
   * Task #2670 follow-up — host's share of `(baseRentalFee + deliveryFee)`,
   * already including any Pro/Premier tier bonus and capped at 1.
   * Surfaces the same value the api-server uses in
   * `computeOwnerEarn(baseRentalFee, deliveryFee, ownerShare)` so the
   * "Request a change" Proposed preview can recompute `ownerEarn` from
   * the canonical formula instead of crediting 100% of the override
   * delta and overstating the host payout.
   */
  ownerShare: number;
  /** Task #2271 — host-paid insurance priced for an authenticated trip change.
   * These fields are returned only to the booking host by the adjustment
   * quote endpoint; they are deliberately distinct from renter insurance. */
  hostInsuranceEnabled?: boolean;
  hostInsuranceFee?: number;
  hostInsurancePolicy?: HostInsurancePolicySnapshot | null;
  ownerEarnBeforeHostInsurance?: number;
  bookingFee: number;
  paymentType: "full" | "partial";
  platformPaymentDue: number;
  dailyRateBreakdown: Array<{ date: string; rate: number }>;
  eventDailyCapped: boolean;
  eventDailyCapRate: number;
  eventHoursRequested: number;
  /**
   * Additive alias of `eventHoursRequested` (task #2274). Always present
   * on responses from the current API; kept optional here only because
   * persisted/older payloads may pre-date the alias.
   */
  hoursRequested?: number;
  // Explicit billing-mode signal for the rental-fee line. See task #826 and
  // the matching comment in artifacts/api-server/src/routes/bookings.ts.
  rentalFeeBilling: "hourly" | "daily-capped" | "daily-flat" | "daily" | "daily-custom";
  rentalFeeRate: number;
  instantBooking: boolean;
  /** Server-authoritative Stripe Elements capture contract for this quote. */
  expectedCaptureMethod?: "automatic_async" | "manual";
  currency: string;
  // Task #2595 — capture-currency snapshot. `chargeCurrency` is the
  // currency the renter's card is actually billed in (full-pay markets:
  // same as `currency`; partial-pay markets: the admin-set USD or EUR).
  // `chargeAmount` is `platformPaymentDue` converted at `fxRate` and
  // rounded to whole units in the charge currency. `fxRate` is the
  // locked rate (1.0 when no conversion). All five fields are optional
  // so older persisted quote payloads continue to typecheck.
  chargeCurrency?: string;
  chargeAmount?: number;
  fxRate?: number;
  fxSource?: string;
  fxFetchedAt?: string;
  stripeRegion?: string;
  stripePublishableKey?: string;
  climateOffsetAmount: number;
  payLater: {
    available: boolean;
    chargeAmount: number;
    chargeDate: string | null;
    daysUntilCharge: number;
  };
  // Task #1495: US sales-tax estimate. `applicable=false` for non-US cars
  // or when feature_us_stripe_tax is OFF. When applicable, `amount` is an
  // ESTIMATE based on the car pickup ZIP — the final amount is computed by
  // Stripe Tax at PI confirmation against the renter's billing address.
  tax?: {
    applicable: boolean;
    amount: number;
    currency: string;
    inclusive: false;
    source: "estimate" | "billing_estimate" | "none";
    calculation_id: string | null;
    calculationId: string | null;
    breakdown?: Array<{
      jurisdiction: string;
      display_name: string;
      amount: number;
      percentage: number | null;
      tax_type: string | null;
    }>;
    note: string;
    // Task #1724: explicit lifecycle status of the tax line so the UI
    // can render an honest in-flight / unavailable state instead of
    // inferring from `amount > 0` (which collapses "pending estimate"
    // and "Stripe Tax error" into the same dash). `"none"` only
    // appears alongside `applicable: false` and tells the UI to skip
    // the row entirely; older API responses that pre-date this field
    // will be `undefined` and the UI falls back to the legacy
    // amount-based heuristic.
    status?: "ok" | "pending" | "unavailable" | "none";
    /** Machine-readable diagnostic that pairs with `status`. */
    reason?: string | null;
  };
  // Task #1495: server-computed canonical totals incl. tax, plus
  // merchant-of-record string per car country.
  totalIncludingTax?: number;
  platformPaymentDueIncludingTax?: number;
  merchantOfRecord?: string;
  // Final tax fields surfaced on the trip-detail payload (filled by the
  // webhook once payment succeeds; null otherwise).
  taxAmount?: number | null;
  taxCalculationId?: string | null;
  // Task #5968: the REAL amount charged to the renter's card (Stripe
  // settlement) and its currency, read off the succeeded op=1 billing row.
  // For partial-payment bookings this is the platform amount in USD (the
  // rental fee is paid directly to the host), so the receipt reports what we
  // actually collected instead of the full local total. Null when no
  // succeeded charge row exists (legacy/imported, unreconciled).
  platformChargeAmount?: number | null;
  platformChargeCurrency?: string | null;
  // Task #2379: per-car suggested-pricing floors so the "Request a
  // change" modal (RequestChangeModal) can enforce a minimum
  // owner-overridable rental fee. Computed by the server from the same
  // `computeSuggestedPricing` snapshot that drives host pricing
  // recommendations and the listing-validator bounds. Optional —
  // unauthenticated callers and older API responses may omit it.
  pricingFloors?: {
    minDailySelfDrive: number;
    minDailyEvent: number;
    minHourlyChauffeured: number;
    currency: string;
  } | null;
  // Task #1006: Renter-verification precheck advisory. Server-side
  // POST /bookings remains the source of truth; this lets the UI render
  // the inline phone-verification panel proactively (and pre-fill it with
  // the renter's existing phone) instead of waiting for the booking call
  // to fail with `phone_unverified`. Absent for unauthenticated quotes.
  verification?: {
    rental_type: "driving" | "chauffeured" | "event";
    requirements: { requires_email: boolean; requires_phone: boolean };
    ok: boolean;
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
    phone_verified?: boolean;
    email_verified?: boolean;
    phone?: string | null;
  };
}

/** Raw wire aliases from GET /bookings/:tripId/trip-adjustment/quote. */
export interface TripAdjustmentQuotePayload extends Partial<PriceQuote> {
  financialAudience?: "host";
  hostDisplayTotal?: number;
  rental_fee?: number;
  service_fee?: number;
  insurance_fee?: number;
  delivery_fee?: number;
  deposit?: number;
  payment_due?: number;
  owner_earn?: number;
  owner_earn_before_host_insurance?: number;
  host_insurance_enabled?: boolean;
  host_insurance_fee?: number;
  host_insurance_policy_snapshot?: HostInsurancePolicySnapshot | null;
  insurance_package_name?: string | null;
  insurance_policy_snapshot?: InsurancePolicySnapshot | null;
  policy_refund_preview?: number | null;
  payment_type?: "full" | "partial";
  total_including_tax?: number;
  pricing_floors?: {
    min_daily_self_drive?: number;
    min_daily_event?: number;
    min_hourly_chauffeured?: number;
    currency?: string;
  } | null;
  num_days?: number;
  rental_type?: string;
  hours_requested?: number;
  event_hours_requested?: number;
  hostInsurance?: {
    enabled?: boolean;
    fee?: number;
    ownerEarn?: number;
    policy?: HostInsurancePolicySnapshot | null;
  } | null;
}
export interface InsurancePolicySnapshot {
  kind: "ny_crum";
  name: string;
  limits: {
    liability_csl: number;
    um_uim: number;
    pip: number;
    apd_cap: number;
  };
  deductible: number;
  liability_per_day: number;
  apd_per_day: number;
  daily_total: number;
  stated_value_used: number;
  stated_value_capped: boolean;
  num_days: number;
  total_charge: number;
}

export interface InsurancePackageWithPricing {
  id: number;
  name: string;
  owner_liability_coverage: string;
  renter_liability_coverage: string;
  physical_deductible: string | null;
  liability_deductible: string | null;
  full_comp: boolean;
  customer_support: boolean;
  dailyRate: number;
  recommended: boolean;
  // Surfaced by GET /insurance-packages/pricing when the package
  // can't be priced for this vehicle (e.g. "Good" is capped at
  // $55k insured value). UI uses this to grey out the option and
  // show inline advice instead of trying to select it and being
  // kicked out by a backend quote error.
  blocked?: boolean;
  blockedReason?: string;
  /**
   * Task #2264 — populated when this is the synthetic NY Crum coverage
   * card. Carries the locked policy data and the per-day liability/APD
   * split so the UI can render the disclosure inline without a second
   * round-trip to the server.
   */
  policy?: InsurancePolicySnapshot | null;
  dailyBreakdown?: {
    liability: number;
    apd: number;
    apdLine: string;
  } | null;
}

export interface ClaimSnapshot {
  carId: number;
  pickUp: string | null;
  dropOff: string | null;
  rentalType: string | null;
  bookingType: string | null;
  currency: string;
  paymentDue: number;
  paymentType: "full" | "partial";
  platformPaymentDue: number;
  ownerEarn: number;
  deposit: number;
  rentalFee: number;
  serviceFee: number;
  insuranceFee: number;
  deliveryFee: number;
  car?: { make: string | null; model: string | null; year: number | null } | null;
  owner?: { firstName: string | null; lastName: string | null } | null;
}

export interface ClaimInviteResponse {
  // Returned by GET /bookings/claim/:token
  state?: "claimable" | "already_claimed" | "cancelled" | "expired";
  claimedByOther?: boolean;
  isCurrentRenter?: boolean;
  // Returned by POST /bookings/claim/:token
  success?: boolean;
  alreadyClaimed?: boolean;
  // Common
  tripId: number;
  publicId: string;
  snapshot: ClaimSnapshot;
  nextStep?: { paymentRoute: string; tripPublicId: string };
}

export interface BookingResult {
  captureMethod?: "automatic" | "automatic_async" | "manual" | null;
  tripId: number;
  publicId: string;
  status: string;
  quote: PriceQuote;
  clientSecret: string | null;
  requiresPayment: boolean;
  stripePublishableKey?: string;
  climateOrderId?: string | null;
}

export interface TripData {
  bookingClock?: { started: boolean; inProgress: boolean; pickup: string | null; return: string | null };
  id: number;

  public_id: string;

  car_id: number;

  renter_id: string;

  owner_id: string;

  started_at: string | null;

  pick_up: string | null;

  drop_off: string;

  status: string;
  /** Host acceptance timestamp; a request hold is renter-actionable only after this is set. */

  approved_at?: string | null;

  booking_type: string;

  trip_type: string;

  rental_type: string | null;

  event_type: string | null;

  billing_currency_code: string;

  local_currency_code?: string | null;

  payment_due: number | null;

  owner_earn: number | null;

  rental_fee: number | null;

  service_fee: number | null;

  insurance_fee: number | null;

  delivery_fee: number | null;

  deposit: number | null;

  insurance_package: string | null;

  insured_value: number | null;

  security_deposit_status: string | null;
  depositSummary?: {
    eligible: true;
    state: "pending" | "processing" | "held" | "action_required" | "captured" | "released" | "refunded" | "unavailable";
    amount: number | null;
    currency: string | null;
    scheduledAt: string | null;
    failedAt: string | null;
    failureReason: string | null;
    retryDeadline: string | null;
    recoveryAvailable: boolean;
    recoveryUnavailableReason: string | null;
    attemptsRemaining: number;
  } | null;

  offer_expiration_date: string | null;

  created_at: string;

  updated_at: string;

  completed_at?: string | null;

  cancelled_at: string | null;

  cancelled_reason_id: number | null;
  /** Canonical cancellation outbox reason, projected only for the authorized trip participants. */

  cancellationReasonCode?: "us_self_drive_driver_cutoff" | null;

  payment_status: string | null;
  // Task #4754: the HOST's payout state (smallint), distinct from the
  // guest-side `payment_status`. 0=scheduled, 2=paid (transfer fired),
  // 3=held by admin, 4=parked/awaiting Connect setup. Drives the host
  // "Payout" chip so a renter-paid-but-not-yet-travelled trip reads
  // "Scheduled", never "Paid".

  payout_status?: number | null;
  // Task #1495: optional final US sales tax line, populated by the
  // payment_intent.succeeded webhook. Null on non-US / flag-OFF /
  // pre-task bookings.

  taxAmount?: number | null;

  taxCalculationId?: string | null;
  // Task #5968: the REAL amount charged to the renter's card (Stripe
  // settlement) and its currency, read off the succeeded op=1 billing row.
  // For partial-payment bookings this is the platform amount in USD (the
  // rental fee is paid directly to the host), so the receipt reports what we
  // actually collected instead of the full local total. Null when no
  // succeeded charge row exists (legacy/imported, unreconciled).

  platformChargeAmount?: number | null;

  platformChargeCurrency?: string | null;
  // Task #396: true when a US driving rental is held in pending_verification
  // until the renter completes Didit licence verification.

  verification_hold?: boolean | null;
  /** US self-drive primary state for the booking renter/host. Other invited
   * drivers receive only their own verification projection, never this roster. */

  primaryDriver?: {
    clearanceDeadline?: string | null;
    eligible: boolean;
    choice: "unresolved" | "renter" | "nominated";
    clearance?: DriverRosterClearance;
    identity?: { name: string | null; verifiedFullName: string | null; licenceVerified?: boolean } | null;
    email?: string | null;
    invitation: {
      email: string | null;
      status: string;
      expiresAt: string | null;
      isExpired: boolean;
      acceptedAt?: string | null;
      confirmedAt?: string | null;
    } | null;

  } | null;

  canSelfAddAdditionalDriver?: boolean;
  additionalDriverRoster?: Array<{
    assignmentId: number;
    role: "additional";
    invitedEmail?: string | null;
    identity?: { name: string | null; verifiedFullName: string | null; licenceVerified?: boolean } | null;
    status: string;
    clearance: DriverRosterClearance;
    acceptedAt: string | null;
    confirmedAt: string | null;
    revokedAt: string | null;
    expiresAt: string | null;
    createdAt: string | null;

  }> | null;
  /** Task #8210 — host-facing US chauffeured/event alternate-driver state,
   * fully resolved server-side. Null for renters, self-drive, non-US, and
   * ended bookings with no invitation history. `state` is host_default until
   * an invite exists; "confirmed" only after acceptance AND verification. */

  hostDriverAssignment?: {
    eligible: true;
    editable: boolean;
    state:
      | "host_default"
      | "invited"
      | "accepted"
      | "verification_required"
      | "confirmed"
      | "rejected"
      | "expired"
      | "revoked";
    invitation: {
      email: string | null;
      status: string;
      expiresAt: string | null;
      acceptedAt: string | null;
      confirmedAt: string | null;
      isExpired: boolean;
    } | null;
  } | null;
  num_passengers: number | null;

  work_trip?: boolean | null;

  business_name?: string | null;

  vat_number?: string | null;

  message_to_owner?: string | null;

  occasion?: string | null;
  // Frozen-at-confirmation booking terms (task #393). These mirror what the
  // backend uses to compute refunds and what the renter agreed to at checkout.

  payment_type?: "full" | "partial" | null;

  payment_timing?: "pay_now" | "pay_later" | null;

  rate_plan?: "flexible" | "non_refundable" | null;

  refund_policy_snapshot?: {
    version: number;
    rate_plan: "flexible" | "non_refundable";
    owner_cancellation_refund_pct: number;
    renter_cancellation_tiers: Array<{ min_hours_before_pickup: number; refund_pct: number }>;
  } | null;
  payment_schedule_snapshot?: unknown | null;
  // Task #969: renter-captured booking inputs that were previously dropped
  // by the POST handler. requested_hours is the chauffeur/event "hours";
  // delivery_distance / airport_code travel with delivered self-drives;
  // climate_offset_opt_in records the renter ticking the climate add-on.
  // times_captured distinguishes a real renter-picked pickup/dropoff time
  // from a legacy date-only row that just happens to render midnight UTC.

  requested_hours?: number | null;

  delivery_distance?: number | null;

  airport_code?: string | null;

  climate_offset_opt_in?: boolean | null;

  times_captured?: boolean | null;
  // Task #975: pickup-vs-delivery target on the trip itself, used by the
  // suggest-a-change modals to seed the location editor with the trip's
  // current selection (so the renter / owner can keep the same airport
  // when they only want to tweak times, etc).

  is_delivery?: boolean | null;

  delivery_address?: string | null;

  pickup_latitude?: number | null;

  pickup_longitude?: number | null;
  // Task #6249: a host-initiated cancellation awaiting admin review. The
  // booking is still live while this is set, so the host detail page renders a
  // persistent "cancellation pending admin review" banner and disables the
  // Cancel button. Null unless the current user is the host of a trip with an
  // open request.

  pendingHostCancellation?: { requestId: number; createdAt: string | null } | null;
  /** Task #5789 — the customer's stored event/pickup address (legacy
   *  "Event From"). Surfaced on the Location row, pick-up card, and map for
   *  chauffeured/event bookings instead of the car's home city. */

  route_from?: string | null;
  // Task #1045: write-once snapshot of the renter's phone state captured at
  // the moment POST /bookings created the trip. `phone_verified_at_booking`
  // is non-null only when the renter was phone_verified at create time;
  // `phone_used_at_booking` is the masked form of the phone we had on file
  // (e.g. "+44 ******1234"), safe to display on host/admin overviews
  // without leaking the full number.

  phone_verified_at_booking?: string | null;

  phone_used_at_booking?: string | null;
  /** Task #3085 — provenance flag; see TripListItem.source. The host
   *  detail page uses this to suppress in-app actions that would
   *  otherwise route through Stripe (propose-change, cancel) and to
   *  render a "Paid externally" payout chip + "Imported" badge. */

  source?: string | null;
  /** Task #5101 — provenance keys for the hardened `isImportedTrip` gate.
   *  `source_id` (legacy-DB pkey on every bulk-migrated row) and
   *  `platform_origin` ("driveshare"/"ds") flag externally-settled bookings
   *  whose mutable `source` may have been clobbered by a backfill. */

  source_id?: number | string | null;

  /** Existing public-view inactivity field; not operational authorization. */
  is_active?: boolean | null;

  platform_origin?: string | null;
  /** Task #972 — legacy chauffeured event-start clock ("HH:MM"), selected from
   *  `trips.hours` under the `event_clock` alias. Used by the booking-overview
   *  display helpers to show the customer event time instead of the stored
   *  host garage-departure time. */

  event_clock?: string | null;
  /** Task #3085 — legacy importer earnings column; falls back to this
   *  when `owner_earn` is null. */

  price_carowner?: number | null;

  cars: {
    id: number;
    make: string;
    model: string;
    year: number;
    slug: string;
    city: string;
    state: string;
    country_code?: string | null;
    price_driving: number | null;
    pickup_instructions: string | null;
    description: string | null;
    latitude: number | null;
    longitude: number | null;
    address?: string | null;
    body?: string | null;
    engine?: string | null;
    color?: string | null;
    colour_name?: string | null;
    mileage?: number | null;
    transmission?: string | null;
    passengers?: number | null;
    is_convertible?: boolean | null;
    has_aircon?: boolean | null;
  } | null;
  renter: { id: string; first_name: string | null; last_name: string | null; email: string | null; profile_image: string | null; phone: string | null; phone_verified?: boolean | null } | null;

  owner: { id: string; first_name: string | null; last_name: string | null; email: string | null; profile_image: string | null; phone: string | null } | null;

  trip_suggestion: Array<{
    id: number;
    rental_fee: number;
    service_fee: number;
    insurance_fee: number;
    delivery_fee: number;
    deposit: number;
    payment_due: number;
    owner_earn: number;
    host_insurance_enabled?: boolean | null;
    host_insurance_fee?: number | null;
    host_insurance_policy_snapshot?: HostInsurancePolicySnapshot | null;
    owner_earn_before_host_insurance?: number | null;
    insurance_package_name: string | null;
    cancellation_policy: string | null;
    refundable_surcharge?: number;
    refundable_surcharge_percent?: number;
    payment_type?: "full" | "partial";
    platform_payment_due?: number;
    booking_fee?: number;
    // Task #8163 — applied discounts persisted at booking time. NULL/absent
    // on historical rows ⇒ surfaces render no discount line.
    coupon_amount?: number | null;
    coupon_code?: string | null;
    pro_owner_discount?: number | null;
    pro_owner_tier?: string | null;

  }>;
  /**
   * Task #8163 — the ONE canonical applied pricing/discount snapshot,
   * lifecycle-aware (an approved later suggestion wins; declined/superseded/
   * expired rows drop out). Prefer this over `trip_suggestion[0]` when
   * rendering discount rows. Null when no discount applied or on older API
   * builds.
   */

  appliedDiscount?: {
    pro_owner_discount: number | null;
    pro_owner_tier: string | null;
    coupon_amount: number | null;
    coupon_code: string | null;
  } | null;
  primaryImage: string | null;

  carImages: string[];

  standoutScore?: {
    one_liner: string | null;
    listing_blurb: string | null;
    era_style: string | null;
    collector_tier: string | null;
  } | null;
  reviewsAggregate?: { count: number; avgRating: number | null } | null;

  ownerResponseStats?: { responseRate: number; avgResponseMinutes: number | null } | null;

  renterVerification?: { license_verified: boolean; license_status: number; on_hold: boolean } | null;
  /** Server-owned booking eligibility/action; absent on older API responses. */

  driverVerification?: {
    eligible: boolean;
    required: boolean;
    state: "excluded" | "not_started" | "in_progress" | "complete";
    /** Booking lifecycle projection; separate from provider verification phase. */
    bookingState?: "excluded" | "awaiting_host_acceptance" | "awaiting_didit" | "awaiting_samba" | "samba_pending" | "accepted_pending" | "payment_pending" | "action_required" | "confirmed";
    /** True only when an existing authorization is valid for this booking. */
    paymentReady?: boolean;
    /** Server-owned actor check; true only for the driver assigned to this booking. */
    assignedDriverIsViewer?: boolean;
  };
  /** Optional booking-scoped renter presentation contract. */

  verificationSummary?: {
    driverClearance?: BookingDriverOverview;
    state: "not_started" | "in_progress" | "in_review" | "action_required" | "failed" | "complete";
    action: "start" | "resume" | "none";
    /** Booking-specific Didit limit reached; keep a visible support route. */
    attemptsExhausted?: boolean;
    /** The booking-specific attempt count could not be checked; no paid start is offered. */
    attemptsUnavailable?: boolean;
    /** An account-level international licence decision, separate from booking acceptance. */
    licenceReviewState?: "international_pending" | "international_approved";
    /** Separate booking-scoped international check; approval does not bypass payment/readiness gates. */
    internationalCheckState?: "not_started" | "pending" | "approved" | "rejected";
    licenceReuploadRequested?: boolean;
    diditFieldsCurrent?: boolean;
    bookingIdVerified?: boolean;
    /** Booking-correlated Samba state, distinct from account-level Didit status. */
    bookingMvrState?: string | null;
    bookingState?: "excluded" | "awaiting_host_acceptance" | "awaiting_didit" | "awaiting_samba" | "samba_pending" | "accepted_pending" | "payment_pending" | "action_required" | "confirmed";
    paymentReady?: boolean;
  } | null;
  insurancePackageDetails?: {
    id: number;
    name: string | null;
    owner_liability_coverage: string | null;
    renter_liability_coverage: string | null;
    full_comp: boolean | null;
    customer_support: boolean | null;
    vip_personal_concierge: boolean | null;
    physical_deductible: string | number | null;
    liability_deductible: string | number | null;
  } | null;
  hasReview: boolean;

  isOwner: boolean;

  isRenter: boolean;

  climateOffsetAmount: number | null;

  climateOrderId: string | null;

  rentalRefundAmount: number | null;

  climateRefundAmount: number | null;

  climateRefundCurrency: string | null;

  insurance_package_name?: string | null;
  /**
   * Task #2264 — populated by GET /bookings/:publicId when the booking
   * was priced under the NY Crum policy. Sourced from the latest
   * trip_suggestions row that carries a snapshot.
   */

  insurance_policy_snapshot?: InsurancePolicySnapshot | null;
  /**
   * Task #2293 — most recent failed-charge reason from the billing table
   * (`error_message` column, set by the payment_intent.payment_failed
   * webhook). Used by the renter-side retry banner on TripDetailPage to
   * tell the renter why their card was declined ("Your card was
   * declined", "Authentication required", etc.). Null when the trip has
   * no failed billing rows.
   */

  paymentFailureReason?: string | null;
  /**
   * Task #6190 — true when a real host-payout settlement artifact (primary
   * op=3 payout billing row or completed payout link) exists for this trip.
   * The owner booking-detail page pairs it with completed_at + isImportedTrip()
   * to show a post-go-live import with no artifact as "No payout recorded"
   * instead of "Settled off-platform". Display-only; no money-state meaning.
   */

  hostPayoutHasArtifact?: boolean;
  hostPayoutSettlement?: HostPayoutSettlement;
  hostSchedule?: HostScheduleDisplay;
  hostStripeHistory?: import("@workspace/api-client-react").HostStripeMovementHistory;
  hostAgreedEarnings?: { amount: number | null; state: "known" | "unknown" | "unavailable" };
  /** Source-recorded coverage only; source IDs are NOT destination coupon FKs. */
  historicalCouponCoverage?: {
    kind: "fully_coupon_covered"; couponAmount: number; currency: string;
    paymentDue: number; rentalCashCollected: number;
    sourceCouponId: number; sourceSuggestionId: number;
  } | null;
  /**
   * Task #8245 — mileage-overage settlement summary for trip participants.
   * Null when no settlement exists or the trip is ineligible for overage
   * charges. Admin-only fields (payment_intent_id, stripe_region,
   * admin_review_required) are excluded from this consumer-facing type.
   */

  mileageOverageSettlement?: RenterMileageOverageSettlement | null;
  /**
   * Task #8440 — host-paid "Better" insurance for eligible US
   * chauffeured/event trips. Populated ONLY for the authenticated host
   * (the `trips` view nulls every one of these columns for the renter at
   * the database layer, so this is never a UI-only guard). Absent/null
   * for every ineligible trip and for the renter, always.
   */

  host_insurance_enabled?: boolean | null;

  host_insurance_fee?: number | null;

  host_insurance_policy_snapshot?: HostInsurancePolicySnapshot | null;

  host_insurance_locked_at?: string | null;

  owner_earn_before_host_insurance?: number | null;
}

/** Task #8440 — GET /bookings/:tripId/host-insurance response shape. */
export type HostInsuranceStatus =
  | {
      eligible: false;
      historical?: boolean;
      reason: string;
      locked: boolean;
      enabled: boolean;
      fee: number | null;
      policy: HostInsurancePolicySnapshot | null;
      ownerEarnBeforeInsurance: number | null;
    }
  | {
      eligible: true;
      historical?: boolean;
      locked: boolean;
      enabled: boolean;
      fee: number | null;
      policy: HostInsurancePolicySnapshot | null;
      ownerEarnBeforeInsurance: number | null;
      quote: { cost: number; policy: HostInsurancePolicySnapshot };
    };

/** Read-only persisted settlement evidence, not live Stripe verification. */
export interface HostPayoutSettlement {
  state: "paid" | "unresolved" | "unavailable";
  method: "transfer" | "manual" | "payout_link" | null;
  amount: number | null;
  currency: string | null;
  needsReview?: boolean;
}

/**
 * Task #8440 (deductible disclosure) — the fixed host-side claims
 * deductible for host-paid Better insurance on eligible US
 * chauffeured/event trips. This is a flat business constant, not a
 * per-quote calculation, so it is not part of `HostInsurancePolicySnapshot`.
 * It is DISTINCT from the renter-purchased "Better" protection-package
 * deductible shown on self-drive checkout/trip insurance cards — never
 * conflate the two in copy or UI.
 */
export const HOST_INSURANCE_DEDUCTIBLE_USD = 2500;
/**
 * Task #8245 — curated mileage-overage settlement type for renter and host
 * consumers. Excludes admin-only fields (payment_intent_id, stripe_region,
 * admin_review_required) that are only exposed via the admin trip-detail API.
 */
export interface RenterMileageOverageSettlement {
  status: string;
  ineligible_reason: string | null;
  country_code: string | null;
  rental_type: string | null;
  frozen_allowance_per_day: number | null;
  frozen_rate_per_mile: number | null;
  pre_trip_mileage: number | null;
  post_trip_mileage: number | null;
  final_charged_days: number | null;
  driven_miles: number | null;
  included_miles: number | null;
  overage_miles: number | null;
  mileage_amount: number | null;
  admin_fee: number | null;
  total_charge: number | null;
  host_share_amount: number | null;
  host_share_status: string | null;
  payment_status: string | null;
  failure_reason: string | null;
  charged_at: string | null;
  created_at: string;
}

export interface TripListItem {
  owner_earn_before_host_insurance?: number | null;
  hostSchedule?: HostScheduleDisplay;
  hostAgreedEarnings?: { amount: number | null; state: "known" | "unknown" | "unavailable" };
  id: number;
  public_id: string;
  car_id: number;
  started_at: string | null;
  pick_up: string | null;
  drop_off: string;
  status: string;
  /** Host acceptance timestamp; a request hold is renter-actionable only after this is set. */
  approved_at?: string | null;
  booking_type: string;
  trip_type: string;
  rental_type: string | null;
  event_type: string | null;
  created_at: string;
  cancelled_at: string | null;
  offer_expiration_date: string | null;
  work_trip?: boolean | null;
  occasion?: string | null;
  payment_timing?: "pay_now" | "pay_later" | null;
  payment_status?: string | null;
  payment_schedule_snapshot?: unknown | null;
  rate_plan?: "flexible" | "non_refundable" | null;
  /** Trip's billing currency (preferred) and local currency
   *  fallback so list cards can render the correct symbol. Resolved via
   *  `resolveTripCurrency(trip)`. */
  billing_currency_code?: string | null;
  local_currency_code?: string | null;
  // Task #396: true when a US driving rental is held in pending_verification.
  verification_hold?: boolean | null;
  /** Server-projected renter licence hold state for the Trips CTA. */
  renterVerification?: { license_verified?: boolean | null; on_hold?: boolean | null } | null;
  /** Optional booking-scoped renter presentation contract. */
  verificationSummary?: {
    driverClearance?: BookingDriverOverview;
    state: "not_started" | "in_progress" | "in_review" | "action_required" | "failed" | "complete";
    action: "start" | "resume" | "none";
    bookingState?: "excluded" | "awaiting_host_acceptance" | "awaiting_didit" | "awaiting_samba" | "samba_pending" | "accepted_pending" | "payment_pending" | "action_required" | "confirmed";
    paymentReady?: boolean;
    /** An account-level international licence decision, separate from booking acceptance. */
    licenceReviewState?: "international_pending" | "international_approved";
    /** Separate booking-scoped international check; approval does not bypass payment/readiness gates. */
    internationalCheckState?: "not_started" | "pending" | "approved" | "rejected";
    licenceReuploadRequested?: boolean;
  } | null;
  // Task #5789 — location fields so list cards resolve the correct booking
  // location (delivery/airport self-drive, or a chauffeured/event booking's
  // stored event address in `route_from`) instead of the car's home city.
  is_delivery?: boolean | null;
  delivery_address?: string | null;
  airport_code?: string | null;
  route_from?: string | null;
  cars: { id: number; make: string; model: string; year: number; slug: string; city?: string; state?: string; country_code?: string | null; price_driving?: number | null } | null;
  renter?: { first_name: string | null; last_name: string | null; email: string | null; profile_image: string | null } | null;
  owner?: { id: string; first_name: string | null; last_name: string | null; profile_image: string | null } | null;
  trip_suggestion: Array<{ rental_fee: number; service_fee: number; insurance_fee: number; delivery_fee: number; deposit: number; payment_due: number; owner_earn: number; payment_type?: "full" | "partial"; platform_payment_due?: number; booking_fee?: number }>;
  trip_suggestions?: Array<{ rental_fee: number; service_fee: number; insurance_fee: number; delivery_fee: number; deposit: number; payment_due: number; owner_earn: number; payment_type?: "full" | "partial"; platform_payment_due?: number; booking_fee?: number }>;
  owner_earn?: number | null;
  initial_request_owner_earn?: number | null;
  /** Task #3085 — provenance flag; `legacy_csv_import` rows are old-platform
   *  bookings with no Stripe artifact (see legacy-import-stripe-guard.ts).
   *  The host list/detail views need this to suppress in-app actions that
   *  would otherwise route through Stripe, and to render a neutral
   *  "Imported booking" badge + a "Paid externally" payout chip. */
  source?: string | null;
  /** Task #5101 — provenance keys for the hardened `isImportedTrip` gate;
   *  see TripData.source_id. */
  source_id?: number | string | null;
  platform_origin?: string | null;
  /** Task #3085 — legacy importer writes the host earnings into
   *  `price_carowner`, not `owner_earn`. The list card falls back to this
   *  when `owner_earn` is null so legacy rows show a number instead of a
   *  blank. */
  price_carowner?: number | null;
  times_captured?: boolean | null;
  primaryImage: string | null;
  /** Task #2394 — true when this booking has a pending guest-initiated
   *  proposal (price/dates/location adjustment) waiting on the host's
   *  response. Drives the "Guest proposed a change · Pending" pill on
   *  the host bookings list and dashboard upcoming-bookings cards. */
  pending_guest_proposal?: boolean;
  pending_guest_proposal_id?: number | null;
  /** True when this booking originated from a win-back offer — the guest
   *  rebooked this car via a `/win-back` deep link after another trip fell
   *  through. Projected by `GET /bookings/owner-bookings` (looked up from
   *  `win_back_offers.converted_trip_id`). Drives the "Win-back" pill on the
   *  host bookings list. Undefined on older API responses. */
  is_winback?: boolean;
  /** Task #8067 — the driver the host assigned to this (chauffeured)
   *  booking. Projected by `GET /bookings/owner-bookings`; null/undefined
   *  when no driver is assigned or on older API responses. Drives the
   *  driver chip on the host bookings list. */
  assigned_driver?: { id: number; name: string | null; photo_url: string | null } | null;
  /** Task #2618 — true when this completed trip already has a review
   *  from the renter, false when it doesn't and the renter can leave
   *  one, undefined when the tab/lookup didn't include a review join
   *  (upcoming / cancelled tabs, older API server). Drives the
   *  "Leave a review" pill on past trip cards. */
  hasReview?: boolean;
  /** Task #5765 — the ONE canonical renter-facing outcome, projected by the
   *  `GET /bookings/my-trips` endpoint so the Trips cards render a consistent
   *  outcome subline + next-action for every later-stage terminal. Source of
   *  truth is the stamped durable notification for payment-side terminals that
   *  keep `status='approved'` (approve capture-fail, pay-later charge-fail),
   *  else derived from a host-driven terminal status (declined/expired). Null
   *  when there is no terminal/failure outcome to surface, or on older API
   *  responses that don't project it. Values mirror the api-server
   *  `RenterOutcome` union. */
  renter_outcome?: string | null;
  /** Task #5765 — the money_status that pairs with `renter_outcome` (e.g.
   *  `nothing_charged`, `authorized_hold`, `refunded`). Advisory for copy; the
   *  subline/CTA are keyed off `renter_outcome`. */
  money_status?: string | null;
}

/**
 * Task #9508 — exact response shape for GET /bookings/my-driving.
 */
export interface MyDrivingTrip {
  key: string;
  car: {
    name: string;
    imageUrl: string | null;
  };
  rentalType: string;
  startAt: string;
  endAt: string;
  timezone: string | null;
  timesCaptured: boolean | null;
  approval: "approved_to_drive" | null;
  assignmentId?: string;
  progress?: import("@workspace/copy").AccountDrivingProgress;
}

export interface CancellationReason {
  id: number;
  reason: string;
  details_required: boolean;
  applies_to: string;
}

export interface AvailabilityFollowUp {
  tripId: number;
  carId: number;
  status: string;
}

export interface AvailabilityFollowUpResponse {
  followUp?: {
    state?: string;
    suppressed?: boolean;
  } | null;
  availabilityFollowUp?: AvailabilityFollowUp | null;
}

export interface DeclineBookingResponse {
  success: boolean;
  idempotent?: boolean;
  availabilityFollowUp?: AvailabilityFollowUp | null;
}

export interface AvailabilityFollowUpSuppressionResponse {
  success: boolean;
  idempotent?: boolean;
}

export interface CancelBookingResult {
  success: boolean;
  withdraw?: boolean;
  /** Task #6249 — a HOST cancellation no longer cancels the booking outright;
   *  it records a pending admin-review request. When true, the booking is still
   *  active, the renter has NOT been notified/refunded, and an admin must
   *  approve the request before the cancellation takes effect. */
  pendingReview?: boolean;
  /** Id of the created `host_cancellation_requests` row when `pendingReview`. */
  requestId?: number;
  refund: {
    amount: number;
    currency: string;
    reason: string;
    policy: string;
    netCapturedBeforeCancellation: number;
    climateRefundAmount: number;
    climateRefundCurrency: string | null;
    /** Per-component refund breakdown (task #395). Present on cancellations
     *  that flowed through the component-aware engine; null for legacy
     *  refund records. */
    breakdown?: RefundBreakdownPayload | null;
  } | null;
}

export interface RefundComponentMap {
  rental_fee: number;
  booking_fee: number;
  delivery_fee: number;
  insurance_fee: number;
  service_fee: number;
}

export interface RefundBreakdownPayload {
  version: 1;
  actor: "renter" | "owner";
  refunded: RefundComponentMap;
  retained: RefundComponentMap;
}

export type CancellationActor = "renter" | "owner";
export type CancellationChargeStatus = "none" | "partial" | "captured";
export type CancellationPolicySource = "snapshot" | "suggestion_accepted" | "suggestion_initial" | "missing";

export interface CancellationPolicySummary {
  policy: "flexible" | "non-refundable" | string;
  policyLabel: string;
  summary: string;
  rules: string[];
  cutoffAt: string | null;
  pickUp: string | null;
  currency: string;
  netCaptured: number;
  /**
   * Task #860: server-computed actor + charge state so the UI never has to
   * re-derive who is cancelling or whether anything has been captured. The
   * cancel modal switches its copy on these.
   */
  actor: CancellationActor;
  chargeStatus: CancellationChargeStatus;
  /**
   * Where the policy came from. "missing" indicates the booking has neither
   * a snapshot nor a recorded suggestion policy — the cancel route will 422
   * in this state.
   */
  policySource: CancellationPolicySource;
  refundPreview: {
    amount: number;
    /** Stable machine reason code (e.g. "flexible_more_than_48h_full_refund"). */
    reason: string;
    policy: string;
    breakdown?: RefundBreakdownPayload | null;
  };
}

/**
 * Task #860: client-side companion to the server's `normalizeRatePlan` /
 * `ratePlanFromCancellationPolicy` helper. The single place the renter UI
 * coerces a policy string into a rate plan. Anything that isn't explicitly
 * non-refundable resolves to flexible.
 */
export function normalizeRatePlan(policy: string | null | undefined): "flexible" | "non_refundable" {
  const normalized = String(policy ?? "").toLowerCase();
  if (normalized === "non-refundable" || normalized === "non_refundable") return "non_refundable";
  return "flexible";
}

export interface EarningsData {
  hasHistoricalProjection?: boolean;
  totalsAvailable?: boolean;
  agreedEarningsByCurrency?: Record<string, { amount: number; unknownCount: number }>;
  totalEarned: number;
  thisMonth: number;
  lastMonth: number;
  pendingInEscrow: number;
  /** ISO currency for the aggregated host earnings totals. UI falls
   *  back to "USD" when null on legacy responses. */
  currency?: string | null;
  /** Task #2620 — true when the host's contributing trips span more
   *  than one currency. The totals are then denominated in the host's
   *  preferred currency and the UI shows a disclosure. */
  currencyIsMixed?: boolean;
  /** Task #4160 — total host earnings missed by declining bookings
   *  (sum of owner earnings across the host's `declined` trips),
   *  denominated in the rollup `currency`. Omitted on legacy responses. */
  declinedMissed?: number;
  /** Task #4160 — count of the host's declined bookings, shown in the
   *  sub-label of the "Missed (declined)" card. */
  declinedCount?: number;
  /** Task #4218 — pre-aggregated monthly earnings series for the
   *  Earnings Overview chart. A continuous, year-aware trailing window of
   *  calendar months (currently 6), zero-filled and sorted oldest→newest.
   *  `month` is a `YYYY-MM` key (so Jun 2025 and Jun 2026 are distinct);
   *  `earnings` is the host owner-earnings total for that month in the
   *  rollup `currency`. Omitted on legacy API responses — the page falls
   *  back to deriving the series client-side from `recentTransactions`. */
  monthlyEarnings?: Array<{ month: string; earnings: number }>;
  recentTransactions: Array<{
    trip_id: number;
    completed_at: string;
    /** Task #6190 — the CANONICAL completion date (raw `completed_at`, no
     *  started/created fallback) used ONLY for the imported-payout go-live
     *  gating decision, so a settled-vs-unsettled call is never made off a
     *  display substitute. Older API responses omit this; a missing value is
     *  treated as pre-go-live (settled) by isImportedPayoutSettled. */
    settlement_completed_at?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    status?: string | null;
    owner_earn: number;
    rental_fee: number;
    service_fee: number;
    payment_due: number;
    /** Task #2876 — four-branch split. "partial" means the card was
     *  only charged the platform fee and the host receives the rest
     *  off-platform. Older API responses omit this; UI treats missing
     *  as "full". */
    payment_type?: "full" | "partial";
    /** Task #2876 — for partial-pay trips, the portion the platform
     *  actually charged the renter's card. Surfaced as "Collected by
     *  platform" in the host dashboard breakdown. */
    platform_payment_due?: number;
    currency?: string;
    car?: string | null;
    public_id?: string | null;
    /** Task #6190 — imported booking (no BAC Stripe artifact). Older API
     *  responses omit this; UI treats missing as native. */
    imported?: boolean;
    /** Task #6190 — a real host-payout settlement artifact (primary op=3
     *  payout billing row or completed payout link) exists. Paired with
     *  `imported` + `completed_at` to distinguish a genuinely off-platform
     *  settled import from a post-go-live auto-stamped-paid one. Display-only. */
    has_settlement_artifact?: boolean;
    hostPayoutSettlement?: HostPayoutSettlement;
    hostSchedule?: HostScheduleDisplay;
    agreed_owner_earn?: number | null;
  }>;
  pendingPayoutCount: number;
  completedPayoutCount: number;
  // Task #1190 — projected escrow releases. The API returns one row per
  // in-escrow trip with the expected payout date (drop_off + payout
  // hold) plus a coarse bucket so the UI can group without re-deriving.
  upcomingPayouts?: Array<{
    agreed_owner_earn?: number | null;
    trip_id: number;
    public_id: string | null;
    status: string;
    end_date: string | null;
    expected_payout_at: string | null;
    bucket: "this_week" | "next_30_days" | "later";
    owner_earn: number;
    currency: string;
    car: string | null;
  }>;
  // Task #1093 — manual (Tier 4 / NZ) host payouts shown alongside
  // Stripe-driven entries on the Earnings page.
  manualPayouts?: Array<{
    id: number;
    trip_id: number;
    trip_public_id: string | null;
    amount: number;
    currency: string;
    amount_usd: number | null;
    payment_method: string;
    payment_reference: string;
    paid_at: string;
    has_discrepancy: boolean;
    discrepancy_reason: string | null;
    expected_amount: number | null;
    expected_currency: string | null;
  }>;
  manualPayoutBanner?: string | null;
}

// Task #1093 (review fix) — payload returned by
// GET /me/payouts/manual/by-trip/:tripId. Mirrors the manual_payouts row
// the host is allowed to see (RLS-restricted to host_id = auth.uid()).
export interface TripManualPayout {
  id: number;
  trip_id: number;
  host_id: string;
  amount: number;
  currency: string;
  amount_usd: number | null;
  fx_rate_to_usd: number | null;
  expected_amount: number | null;
  expected_currency: string | null;
  has_discrepancy: boolean;
  discrepancy_reason: string | null;
  payment_method: string;
  payment_reference: string;
  paid_at: string;
  notes: string | null;
  created_at: string;
}

export interface InspectionImage {
  id: number;
  trip_id: number;
  uploaded_by: string;
  inspection_type: "pre_trip" | "post_trip";
  image_url: string;
  note: string | null;
  created_at: string;
}

export type StripeRegion = "us" | "intl";

/**
 * Frontend mirror of api-server `resolveRegion()`. Cards saved to a Stripe
 * customer in one region cannot be reused in the other, so the renter only
 * sees cards from the region the car is billed in.
 */
export function resolveStripeRegion(countryCode: string | null | undefined): StripeRegion {
  if (!countryCode) return "intl";
  return countryCode.trim().toUpperCase() === "US" ? "us" : "intl";
}

/**
 * The Stripe region a booking's PaymentIntent is actually created in. This
 * MUST mirror the api-server's `carRegion` logic in
 * `routes/bookings/lifecycle/request.ts`: partial-pay markets route by the
 * admin-chosen *capture* currency (USD → US Stripe, EUR → INTL Stripe), not
 * by the car's country, because the car's own country often has no Stripe
 * account. Saved cards live per-region (a card saved in one account cannot be
 * retrieved in the other), so the checkout wallet must be listed for the
 * SAME region the PI will use — otherwise the server's
 * `paymentMethods.retrieve` throws and the booking fails with
 * "Invalid payment method". Full-pay markets keep country-based routing
 * because there chargeCurrency === local currency.
 */
export function resolveBookingStripeRegion(
  countryCode: string | null | undefined,
  opts?: { paymentType?: string | null; chargeCurrency?: string | null },
): StripeRegion {
  const paymentType = (opts?.paymentType ?? "").toLowerCase();
  const chargeCurrency = (opts?.chargeCurrency ?? "").toUpperCase();
  if (paymentType === "partial" && chargeCurrency === "USD") return "us";
  if (paymentType === "partial" && chargeCurrency === "EUR") return "intl";
  return resolveStripeRegion(countryCode);
}

export interface SavedPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault?: boolean;
}

export interface TripAdjustment {
  original_owner_earn?: number | null;
  host_direct_payment_due?: number | null;
  original_host_direct_payment_due?: number | null;
  original_payment_due?: number | null;
  original_card_target?: number | null;
  proposed_card_target?: number | null;
  capture_currency?: string | null;
  pricing_currency?: string | null;
  id: number;
  start_date: string;
  end_date: string;
  rental_fee: number;
  service_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  deposit: number;
  payment_due: number;
  owner_earn: number;
  /** Host-only coverage snapshot/cost for this proposed term set. */
  host_insurance_enabled?: boolean | null;
  host_insurance_fee?: number | null;
  host_insurance_policy_snapshot?: HostInsurancePolicySnapshot | null;
  owner_earn_before_host_insurance?: number | null;
  insurance_package_name?: string | null;
  cancellation_policy?: string | null;
  notes: string | null;
  // Task #2405: "cancelled" — proposer withdrew their own pending request.
  // Task #2583: "payment_failed" — durable state set when the
  // off-session delta charge failed during accept (migration 2380 +
  // GET status precedence). UI surfaces a retry CTA on this state.
  status: "pending" | "accepted" | "declined" | "superseded" | "cancelled" | "expired" | "payment_failed" | "pricing_drift";
  suggested_by: string;
  suggested_by_role: "host" | "guest";
  // counter_of: id of the suggestion this row is a counter-proposal to.
  // null for the original proposal at the start of a negotiation chain.
  counter_of?: number | null;
  // ISO timestamp when this suggestion was superseded by a later counter
  // (i.e. the user submitted a Counter while this one was still pending).
  // Once set, the respond endpoint refuses accept/decline on this row.
  superseded_at?: string | null;
  created_at: string;
  // Task #975: time + location proposed values. NULL ⇒ "no change vs the
  // parent trip" so the chat card can render an old → new diff cleanly
  // without re-querying the trip.
  pickup_at?: string | null;
  dropoff_at?: string | null;
  is_delivery?: boolean | null;
  delivery_distance?: number | null;
  airport_code?: string | null;
  delivery_address?: string | null;
  pickup_latitude?: number | null;
  pickup_longitude?: number | null;
  // Task #2583 — cancellation-policy-driven refund the renter will
  // actually receive on accept for an in-progress shortening. When
  // present this is <= simple net-delta refund and the proposal card
  // displays it instead of the misleading raw delta.
  policy_refund_preview?: number | null;
  // Task #2882 — trip-level net-captured (sum of succeeded op=1
  // rental charges minus sum of succeeded op=5 refunds), denormalized
  // onto every row so the proposal card / sidebar can use it as the
  // captured-net baseline for price-change previews instead of
  // `trip.payment_due`. The trip column can drift above netCaptured
  // when a prior delta path failed or was skipped — using it as the
  // baseline silently absorbs price increases, which is the exact
  // bug this field was added to fix. Optional for backward
  // compatibility with cached responses from before the field was
  // added; callers should fall back to `trip.payment_due` only when
  // this is undefined/null.
  current_net_captured?: number | null;
  // Task #3018 — surfaced so the booking-overview timeline can do the
  // same `pending && expires_at < now()` lapse check the chat's
  // ProposalCard already does, instead of trusting only the stored
  // `status` (which lags until the inngest expiry cron stamps the row).
  expires_at?: string | null;
  // Task #3531 — partial-pay marker for the booking this adjustment
  // belongs to. The renter's card only moves the platform portion on
  // partial bookings, so the sidebar uses this to split the charged-now
  // delta from the owner-direct (at-pickup) delta. Optional for cached
  // rows from before the field was selected.
  payment_type?: "full" | "partial" | null;
}

export const bookingsApi = {
  createHandoverLink(token: string, tripId: number, phase: "pre_trip" | "post_trip"): Promise<{ path: string }> {
    return authFetch(`/bookings/${tripId}/handover-link`, token, {
      method: "POST",
      body: JSON.stringify({ phase }),
    });
  },
  getBookedCarDeliveryOptions(
    token: string,
    tripId: number,
  ): Promise<{
    carId: number;
    delivery_enabled: boolean | null;
    self_drive_delivery_enabled: boolean | null;
    delivery_maximum_distance: number | null;
    delivery_radius_free: number | null;
    delivery_radius_full: number | null;
    deliveryAirports: DeliveryAirport[];
  }> {
    return authFetch(`/bookings/${tripId}/delivery-options`, token);
  },

  // `token` is optional: when provided, the backend will populate the
  // verification block on the response with the renter's current
  // phone_verified / email_verified facts and stored phone — needed for
  // task #1006's inline phone-verification gate at checkout. Anonymous
  // callers (listing page price preview) keep the same behaviour.
  async getQuote(carId: number, dateFrom: string, dateTo: string, rentalType = "driving", insurancePackageId?: number | null, cancellationPolicy?: string, couponCode?: string, hours?: number, deliveryDistance?: number, airportCode?: string, pickupTime?: string, dropoffTime?: string, token?: string | null, includeCarbonOffsetInTax?: boolean, includeFloors?: boolean, bookingId?: number | null, rentalFeeOverride?: number | null, deliveryFeeOverride?: number | null, pickupAddress?: string | null, pickupLatitude?: number | null, pickupLongitude?: number | null, probeToken?: string): Promise<PriceQuote> {
    // Task #5120 — short-circuit a malformed / calendar-impossible date
    // client-side so we never fire the request the server correctly
    // rejects with `400 Invalid date format` (operational Sentry noise
    // from stale sessionStorage / URL date values). `isValidIsoDate`
    // also rejects ISO-shaped-but-impossible dates (e.g. 2026-02-30 that
    // JS would silently roll forward). The thrown `QuoteApiError` carries
    // status 400 + code `INVALID_DATE` so callers treat it as a handled
    // inline rejection and Sentry's `beforeSend` drops it.
    if (!isValidIsoDate(dateFrom) || !isValidIsoDate(dateTo)) {
      throw new QuoteApiError("Invalid date format. Use YYYY-MM-DD", "INVALID_DATE", undefined, 400);
    }
    const params = new URLSearchParams({
      car_id: String(carId),
      date_from: dateFrom,
      date_to: dateTo,
      rental_type: rentalType,
    });
    if (hours) params.set("hours", String(hours));
    if (probeToken) params.set("probe_token", probeToken);
    if (insurancePackageId) params.set("insurance_package_id", String(insurancePackageId));
    if (cancellationPolicy) params.set("cancellation_policy", cancellationPolicy);
    if (couponCode) params.set("coupon_code", couponCode);
    // Zero is a valid resolved distance (renter at the car); only drop missing/invalid values.
    if (deliveryDistance != null && Number.isFinite(deliveryDistance) && deliveryDistance >= 0) params.set("delivery_distance", String(deliveryDistance));
    if (airportCode) params.set("airport_code", airportCode);
    if (pickupAddress) params.set("delivery_address", pickupAddress);
    if (pickupLatitude != null) params.set("pickup_latitude", String(pickupLatitude));
    if (pickupLongitude != null) params.set("pickup_longitude", String(pickupLongitude));
    if (pickupTime) params.set("pickup_time", pickupTime);
    if (dropoffTime) params.set("dropoff_time", dropoffTime);
    // Task #1495 follow-up: forward the renter's climate-offset opt-in
    // so the server adds the offset to the taxable basis when quoting,
    // matching what the same renter will be charged at checkout.
    if (includeCarbonOffsetInTax) params.set("include_carbon_offset_in_tax", "true");
    // Task #2379: only the owner-facing Request a change modal needs
    // per-car pricing floors, so we gate the (DB-heavy) snapshot
    // server-side behind this opt-in flag.
    if (includeFloors) params.set("include_floors", "1");
    // Task #2940 — change-request flow on in-progress trips re-quotes
    // against the trip's original (past) start date. Pass the trip id
    // so the server can verify the past `date_from` matches that
    // trip's `started_at` before relaxing the today-floor guard.
    if (bookingId != null) params.set("booking_id", String(bookingId));
    // Task #2959 — when the host edits the rental / delivery fee in
    // the Build Your Quote modal, forward the typed values as
    // overrides so the server runs the same canonical `calculateQuote`
    // path the accept handler uses. The modal previously back-derived
    // the service-fee rate from the original quote and multiplied
    // locally — that drifted by 1p on clean 15% rates and worse on
    // any quote that carried a surcharge / coupon / pro-owner share.
    if (rentalFeeOverride != null) params.set("rental_fee_override", String(rentalFeeOverride));
    if (deliveryFeeOverride != null) params.set("delivery_fee_override", String(deliveryFeeOverride));
    // Task #3228 / #3502 — attach a pre-warmed bot-challenge token if one
    // is already in hand, WITHOUT blocking the price request on the widget
    // (a missing token is fine: the server fails open when the feature is
    // off and otherwise replies 403 `turnstile_required`, handled below).
    // Web checkout collects/verifies phone on confirmation, never at setup.
    // Declare the same timing contract for quote and protected session create.
    params.set("checkout_phone_verification", "post-booking-v1");
    const path = `/bookings/quote?${params.toString()}`;
    try {
      return await apiFetch(path, token, takeReadyTurnstileToken());
    } catch (err) {
      // Only when the server actively demands a challenge do we pay the
      // cost of obtaining one and retry once. This keeps the common path
      // instant while still satisfying an enabled Turnstile gate.
      if (err instanceof QuoteApiError && err.code === "turnstile_required") {
        const retryToken = await getTurnstileToken();
        if (retryToken) return await apiFetch(path, token, retryToken);
      }
      throw err;
    }
  },

  /**
   * Task #2271 — quote a proposed change against the authenticated booking.
   * Unlike the public listing quote, the server can use the trip's locked
   * renter policy and host-paid coverage snapshot when repricing changed
   * dates. The server also role-filters private host insurance data.
   */
  async getTripAdjustmentQuote(
    token: string,
    tripId: number,
    params: {
      dateFrom: string;
      dateTo: string;
      rentalType?: string;
      hours?: number;
      pickupAt?: string | null;
      dropoffAt?: string | null;
      isDelivery: boolean;
      deliveryDistance?: number | null;
      airportCode?: string | null;
      includeFloors?: boolean;
      rentalFeeOverride?: number | null;
      deliveryFeeOverride?: number | null;
      pickupAddress?: string | null;
      pickupLatitude?: number | null;
      pickupLongitude?: number | null;
    },
  ): Promise<TripAdjustmentQuote> {
    if (!isValidIsoDate(params.dateFrom) || !isValidIsoDate(params.dateTo)) {
      throw new QuoteApiError("Invalid date format. Use YYYY-MM-DD", "INVALID_DATE", undefined, 400);
    }
    const query = new URLSearchParams({
      date_from: params.dateFrom,
      date_to: params.dateTo,
      is_delivery: params.isDelivery ? "true" : "false",
    });
    if (params.pickupAt) query.set("pickup_at", params.pickupAt);
    if (params.dropoffAt) query.set("dropoff_at", params.dropoffAt);
    // Nullable location terms use presence semantics: undefined inherits the
    // trip value, null explicitly clears it, and numeric zero is meaningful.
    if (params.deliveryDistance !== undefined) {
      query.set("delivery_distance", params.deliveryDistance === null ? "" : String(params.deliveryDistance));
    }
    if (params.airportCode !== undefined) query.set("airport_code", params.airportCode ?? "");
    if (params.includeFloors) query.set("include_floors", "1");
    if (params.rentalFeeOverride != null) query.set("rental_fee", String(params.rentalFeeOverride));
    if (params.deliveryFeeOverride != null) query.set("delivery_fee", String(params.deliveryFeeOverride));
    if (params.pickupAddress !== undefined) query.set("delivery_address", params.pickupAddress ?? "");
    if (params.pickupLatitude !== undefined) {
      query.set("pickup_latitude", params.pickupLatitude === null ? "" : String(params.pickupLatitude));
    }
    if (params.pickupLongitude !== undefined) {
      query.set("pickup_longitude", params.pickupLongitude === null ? "" : String(params.pickupLongitude));
    }
    const payload = await authFetch<TripAdjustmentQuotePayload>(
      `/bookings/${tripId}/trip-adjustment/quote?${query.toString()}`,
      token,
    );
    const rawFloors = payload.pricingFloors ?? (payload.pricing_floors
      ? {
          minDailySelfDrive: Number(payload.pricing_floors.min_daily_self_drive),
          minDailyEvent: Number(payload.pricing_floors.min_daily_event),
          minHourlyChauffeured: Number(payload.pricing_floors.min_hourly_chauffeured),
          currency: payload.pricing_floors.currency ?? payload.currency ?? "USD",
        }
      : null);
    const floors = rawFloors
      && [rawFloors.minDailySelfDrive, rawFloors.minDailyEvent, rawFloors.minHourlyChauffeured]
        .every((value) => Number.isFinite(value) && value >= 0)
      && typeof rawFloors.currency === "string" && rawFloors.currency.length > 0
      ? rawFloors
      : null;
    const rentalFee = Number(payload.rentalFee ?? payload.rental_fee);
    const deliveryFee = Number(payload.deliveryFee ?? payload.delivery_fee);
    const serviceFee = Number(payload.serviceFee ?? payload.service_fee);
    const insuranceFee = Number(payload.insuranceFee ?? payload.insurance_fee);
    const hostResponse = payload.financialAudience === "host";
    const paymentDue = hostResponse ? undefined : Number(payload.paymentDue ?? payload.payment_due);
    if (![rentalFee, deliveryFee, serviceFee, insuranceFee].every(Number.isFinite) ||
      (!hostResponse && !Number.isFinite(paymentDue))) {
      throw new QuoteApiError("The change quote response was incomplete. Please try again.", "INVALID_QUOTE_RESPONSE");
    }
    return {
      rentalType: payload.rentalType ?? payload.rental_type ?? params.rentalType ?? "driving",
      numDays: payload.numDays ?? payload.num_days ?? Math.max(1, Math.ceil(
        (new Date(`${params.dateTo}T00:00:00Z`).getTime() - new Date(`${params.dateFrom}T00:00:00Z`).getTime()) / 86_400_000,
      )),
      rentalFee,
      baseRentalFee: Number(payload.baseRentalFee ?? rentalFee),
      serviceFee,
      insuranceFee,
      insurancePackageName: payload.insurancePackageName ?? payload.insurance_package_name ?? null,
      insurancePolicy: payload.insurancePolicy ?? payload.insurance_policy_snapshot ?? null,
      deliveryFee,
      deposit: Number(payload.deposit ?? 0),
      paymentDue,
      financialAudience: payload.financialAudience,
      hostDisplayTotal: payload.hostDisplayTotal,
      totalIncludingTax: hostResponse ? undefined : Number(payload.totalIncludingTax ?? payload.total_including_tax ?? paymentDue),
      ownerEarn: payload.ownerEarn ?? payload.owner_earn,
      hoursRequested: payload.hoursRequested ?? payload.hours_requested ?? params.hours,
      eventHoursRequested: payload.eventHoursRequested ?? payload.event_hours_requested ?? params.hours,
      couponDiscount: payload.couponDiscount,
      appliedCoupon: payload.appliedCoupon,
      proOwnerBookingDiscount: payload.proOwnerBookingDiscount,
      proOwnerTier: payload.proOwnerTier,
      tax: payload.tax,
      pricingFloors: floors,
      hostInsuranceEnabled: payload.hostInsuranceEnabled ?? payload.host_insurance_enabled ?? payload.hostInsurance?.enabled,
      hostInsuranceFee: payload.hostInsuranceFee ?? payload.host_insurance_fee ?? payload.hostInsurance?.fee,
      hostInsurancePolicy: payload.hostInsurancePolicy
        ?? payload.host_insurance_policy_snapshot
        ?? payload.hostInsurance?.policy
        ?? null,
      ownerEarnBeforeHostInsurance: payload.ownerEarnBeforeHostInsurance
        ?? payload.owner_earn_before_host_insurance,
      policyRefundPreview: payload.policy_refund_preview,
      pickupProof: payload.pickupProof,
    };
  },

  /**
   * Re-estimate the US sales-tax line against the renter's billing
   * address (ZIP collected on the Payment step). Task #1524 "Option 1":
   * lets the Price details panel show the actual tax amount instead of
   * "calculated at checkout" once the renter has typed their billing
   * ZIP. Returns the same `tax` shape that lives on `PriceQuote.tax`.
   */
  async recalculateTax(input: {
    carId: number;
    billingAddress: { postalCode: string; state?: string | null; country?: string | null };
    amounts: { rental: number; delivery?: number; serviceFee?: number; carbonOffset?: number };
    token?: string | null;
    signal?: AbortSignal;
  }): Promise<{ tax: NonNullable<PriceQuote["tax"]> }> {
    const body = JSON.stringify({
      car_id: input.carId,
      billing_address: {
        postal_code: input.billingAddress.postalCode,
        state: input.billingAddress.state ?? undefined,
        country: input.billingAddress.country ?? "US",
      },
      amounts: {
        rental: input.amounts.rental,
        delivery: input.amounts.delivery ?? 0,
        service_fee: input.amounts.serviceFee ?? 0,
        carbon_offset: input.amounts.carbonOffset ?? 0,
      },
    });
    // Task #8483 — this call already accepts a CALLER-owned `signal`
    // (CheckoutPage aborts a stale in-flight recalculation when a newer one
    // is about to fire). That cancellation must keep throwing the standard
    // `AbortError` unchanged — CheckoutPage's catch treats it as "a newer
    // request is taking over, do nothing". We layer our OWN internal
    // watchdog on top via a combined controller, but only mark the flight
    // as "timed out" when it was OUR timer that fired, so we can throw a
    // distinctly-named `QuoteTimeoutError` for a genuine stall while
    // leaving caller-driven cancellation exactly as it was.
    const combined = new AbortController();
    const startedAt = Date.now();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      combined.abort();
    }, QUOTE_FETCH_TIMEOUT_MS);
    const onCallerAbort = () => combined.abort();
    input.signal?.addEventListener("abort", onCallerAbort);
    try {
      const doFetch = (turnstileToken: string | null) =>
        boundedBookingFetch(`${API_BASE}/bookings/quote/tax`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(input.token ? { Authorization: `Bearer ${input.token}` } : {}),
            ...(turnstileToken ? { [TURNSTILE_HEADER]: turnstileToken } : {}),
          },
          body,
          signal: combined.signal,
        }, "/bookings/quote/tax");
      // Task #3228 / #3502 — attach a pre-warmed bot-challenge token if ready
      // without blocking on the widget; only fetch one (and retry) when the
      // server actively demands a challenge with a 403 `turnstile_required`.
      let res = await doFetch(takeReadyTurnstileToken());
      if (res.status === 403) {
        const errBody = await res.clone().json().catch(() => ({}) as { code?: string });
        if (errBody?.code === "turnstile_required") {
          const retryToken = await getTurnstileToken();
          if (retryToken) res = await doFetch(retryToken);
        }
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `API error ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      if (timedOut && (err as { name?: string } | null)?.name === "AbortError") {
        reportQuoteStall({
          route: "/bookings/quote/tax",
          stage: "tax_calculation",
          elapsedMs: Date.now() - startedAt,
          carId: input.carId,
          billingCountry: input.billingAddress.country ?? "US",
          budgetMs: QUOTE_FETCH_TIMEOUT_MS,
        });
        throw new QuoteTimeoutError();
      }
      throw err;
    } finally {
      clearTimeout(timer);
      input.signal?.removeEventListener("abort", onCallerAbort);
    }
  },

  getInsurancePackagesWithPricing(carId: number, rentalType = "driving"): Promise<{ packages: InsurancePackageWithPricing[] }> {
    return apiFetch(`/insurance-packages/pricing?car_id=${carId}&rental_type=${rentalType}`);
  },

  /**
   * Task #8440 — listing-time host guidance. No car/trip id is required
   * (the wizard may still be creating the listing), so this is the only
   * host-insurance call that works without an existing booking. Returns
   * `eligible: false` for any non-US / non-chauffeured/event combination
   * instead of throwing, so the wizard can quietly hide the section.
   */
  async getHostInsuranceEstimate(
    token: string,
    input: { countryCode: string; state: string; rentalType: "chauffeured" | "event"; insuredValue: number },
  ): Promise<{ eligible: boolean; dailyRate?: number; currency?: string; reason?: string }> {
    const params = new URLSearchParams({
      country_code: input.countryCode,
      state: input.state,
      rental_type: input.rentalType,
      insured_value: String(input.insuredValue),
    });
    return authFetch(`/host-insurance/estimate?${params.toString()}`, token, { method: "GET" });
  },

  /**
   * Task #8440 — host-only read of a trip's current host-insurance state
   * plus a freshly recomputed Better quote. Call this every time the
   * booking-detail page (re)renders the insurance control so the shown
   * quote/payout is never stale relative to a reprice or valuation change.
   * Never call this for a renter-viewed trip — the server 403s anyway,
   * but the UI must not even attempt it outside the host branch.
   */
  async getHostInsuranceStatus(
    token: string,
    tripId: number,
  ): Promise<HostInsuranceStatus> {
    return authFetch(`/bookings/${tripId}/host-insurance`, token, { method: "GET" });
  },

  /** Host-only confirmation of the trip's destination for NY eligibility. */
  async setHostInsuranceDestination(
    token: string,
    tripId: number,
    destination: string,
  ): Promise<{
    destination: string;
    destinationState: string;
    hostInsurance: { eligible: boolean; reason: string | null };
  }> {
    return authFetch(`/bookings/${tripId}/host-insurance/destination`, token, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ destination }),
    });
  },

  /**
   * Task #8440 — the ONLY way to change a trip's host-paid Better
   * insurance. Host-only; the server re-derives eligibility, price, and
   * policy snapshot itself (this call never sends a client-computed
   * price). `eventKey` makes a retried request idempotent — always pass a
   * freshly generated key per intentional click, and reuse the SAME key
   * only when retrying that exact same click after a network failure.
   */
  async setHostInsurance(
    token: string,
    tripId: number,
    enabled: boolean,
    eventKey: string,
  ): Promise<{ hostInsurance: { idempotent: boolean; action?: string; owner_earn?: number; host_insurance_fee?: number } }> {
    return authFetch(`/bookings/${tripId}/host-insurance`, token, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled, event_key: eventKey }),
    });
  },

  // Task #6863: `currency` is the listing's currency_code (e.g. "GBP").
  // When provided and the coupon is fixed-type with a pinned currency, the
  // server rejects mismatches with a clear 400. Omitting currency skips the
  // server-side early check (calculateQuote is the authoritative gate).
  async validateCoupon(token: string, code: string, _carId?: number, rentalType?: string, currency?: string | null): Promise<{ valid: boolean; couponId: number; code: string; discountType: string; discountValue: number; maxDiscount: number | null; currency: string | null }> {
    return authFetch("/bookings/validate-coupon", token, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, rentalType, ...(currency ? { currency } : {}) }),
    });
  },

  // Checkout moderation pre-check — fast, AI-free keyword screen of the
  // renter's "message to host" note, called by the Pay button BEFORE Stripe so
  // an obvious-bad note is caught before any Stripe contact. Returns
  // `{ blocked: true, ... }` only for confident keyword hits; the server's
  // full AI gate at booking submit remains the authoritative backstop.
  async precheckMessageToOwner(
    token: string,
    messageToOwner: string,
  ): Promise<{
    blocked: boolean;
    code?: string;
    message?: string;
    category?: string | null;
    categories?: string[];
    i18n_key?: string;
  }> {
    return authFetch("/bookings/moderate-note", token, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messageToOwner }),
    });
  },

  // Task #3418 — Payment-first checkout. Creates a checkout_session + Stripe
  // PI on the server (no trip row yet), returns the clientSecret so the
  // browser can confirm inline.  After confirmation the Stripe webhook
  // materialises the trip; poll pollCheckoutTrip until tripPublicId is set.
  createCheckoutSession(
    token: string,
    data: {
      carId: number;
      recoverySessionPublicId?: string;
      primaryDriverEmail?: string;
      dateFrom: string;
      dateTo: string;
      rentalType: string;
      hours?: number;
      paymentMethodId?: string;
      insurancePackageId?: number | null;
      cancellationPolicy: string;
      couponCode?: string;
      numPassengers?: number;
      climateOffset?: boolean;
      deliveryDistance?: number;
      airportCode?: string;
      paymentTiming?: "pay_now" | "pay_later";
      pickupTime?: string;
      dropoffTime?: string;
      occasion?: string;
      deliveryAddress?: string;
      pickupLatitude?: number;
      pickupLongitude?: number;
      pickupProof?: string;
      workTrip?: boolean;
      businessName?: string;
      vatNumber?: string;
      messageToOwner?: string;
      // Task #4948 — bearer token of a host "Build a trip" custom quote the
      // renter is accepting as-is. The PFC session endpoint re-validates it,
      // applies the host's override, forces instant booking, and consumes it.
      quoteToken?: string;
      // Task #5598 — win-back offer token from a declined-booking win-back
      // deep-link. Forwarded so the server attributes the conversion.
      winBackToken?: string;
      /** Scoped host availability consent, verified by the server. */
      probeToken?: string;
      // Task #6837 — set true when the always-visible clickwrap/MIT-consent
      // notice was rendered above the Pay button (records the renter's Terms +
      // merchant-initiated-charge authorization).
      checkout_terms_accepted?: boolean;
      checkout_occasion_capability?: "required-v1";
      checkout_driver_choice_capability?: "required-v1";
      renterIsDriver?: boolean | null;
      checkoutJourneyId?: string;
      /** Server-authoritative capture contract from the pre-submit quote. */
      expectedCaptureMethod?: "automatic_async" | "manual";
    },
  ): Promise<{
    sessionPublicId: string;
    clientSecret: string;
    stripePublishableKey: string;
    requiresPayment: boolean;
    state?: "resumed" | "materialized";
    captureMethod?: "automatic" | "automatic_async" | "manual";
    resumed?: boolean;
    tripPublicId?: string | null;
    // Task #5757 — set when the server transparently cleared the renter's OWN
    // stale checkout hold / pending-trip orphan and retried successfully, so
    // the client can reassure them why their earlier stuck attempt is gone.
    recoveredOwnConflict?: boolean;
  }> {
    return authFetch("/checkout-sessions-pfc", token, {
      method: "POST",
      headers: data.checkoutJourneyId
        ? { "x-checkout-money-journey-id": data.checkoutJourneyId }
        : undefined,
      body: JSON.stringify({ ...data, checkout_phone_verification: "post-booking-v1", acquisitionConsent: acquisitionConsent(), bookingAnalyticsContext: getBookingAnalyticsContext() }),
    });
  },

  getCheckoutRecovery(token: string, publicId: string): Promise<{
    tripPublicId?: string;
    sessionPublicId: string;
    carId: number;
    holdUntil: string;
    expired: boolean;
    failureReason: string | null;
    state: import("./checkout-state").SecureCheckoutState;
  }> {
    return authFetch(`/checkout-sessions-pfc/${encodeURIComponent(publicId)}/recovery`, token);
  },

  emailCheckoutRecovery(token: string, publicId: string): Promise<{ delivered: boolean; holdUntil: string }> {
    return authFetch(`/checkout-sessions-pfc/${encodeURIComponent(publicId)}/recovery-email`, token, { method: "POST" });
  },

  // Poll until the Stripe webhook has materialised the trip from the session.
  /** Fail-closed reservation/payment check immediately before confirming. */
  preflightCheckoutSession(
    token: string,
    sessionPublicId: string,
  ): Promise<{ status: string; tripPublicId: string | null; paymentReady?: boolean }> {
    return authFetch(`/checkout-sessions-pfc/${encodeURIComponent(sessionPublicId)}/trip?beforePayment=1`, token);
  },

  pollCheckoutTrip(
    token: string,
    sessionPublicId: string,
  ): Promise<{ status: string; tripPublicId: string | null }> {
    return authFetch(`/checkout-sessions-pfc/${sessionPublicId}/trip`, token);
  },

  // Task #5669 — on-demand materialization fallback. Called when the trip poll
  // window elapses without a materialized trip: the server runs the same
  // idempotent materializer the Stripe webhook uses (only if the pre-auth is
  // actually authorized), so an authorized pre-auth is not stranded until the
  // 15-min reconcile cron. Returns the same shape as pollCheckoutTrip.
  reconcileCheckoutSession(
    token: string,
    sessionPublicId: string,
  ): Promise<{ status: string; tripPublicId: string | null }> {
    return authFetch(`/checkout-sessions-pfc/${sessionPublicId}/reconcile`, token, {
      method: "POST",
    });
  },

  // A definitive browser-side card, 3DS, or wallet failure still has to be
  // checked against Stripe on the server before its PFC calendar hold can be
  // released. The response distinguishes a safe release from a late success,
  // but checkout deliberately keeps the renter on the retry form either way.
  releaseFailedCheckoutSession(
    token: string,
    sessionPublicId: string,
  ): Promise<{
    outcome: "released" | "already_terminal" | "retained" | "materialized";
    tripPublicId: string | null;
  }> {
    return authFetch(`/checkout-sessions-pfc/${sessionPublicId}/release-failed-payment`, token, {
      method: "POST",
    });
  },

  createBooking(token: string, data: {
    carId: number;
    dateFrom: string;
    dateTo: string;
    rentalType: string;
    hours?: number;
    paymentMethodId?: string;
    insurancePackageId?: number;
    cancellationPolicy?: string;
    couponCode?: string;
    numPassengers?: number;
    climateOffset?: boolean;
    deliveryDistance?: number;
    airportCode?: string;
    deliveryAddress?: string;
    /**
     * Task #2609 — geocoded coords of the renter-typed delivery
     * address. When provided alongside `deliveryAddress`, the server
     * persists them on the new trip so the host's "Delivery location"
     * map pin lands on the typed address instead of falling back to
     * the car's home location. Both must be sent or neither.
     */
    pickupLatitude?: number;
    pickupLongitude?: number;
    pickupProof?: string;
    workTrip?: boolean;
    businessName?: string;
    vatNumber?: string;
    paymentTiming?: "pay_now" | "pay_later";
    messageToOwner?: string;
    pickupTime?: string;
    dropoffTime?: string;
    occasion?: string;
    renterIsDriver?: boolean | null;
    /**
     * Task #4316 — host "Build a trip" quote token. When present (renter
     * arrived from a host-built quote and hasn't edited any priced term),
     * the server applies the host's overridden rental + delivery fees and
     * confirms instantly (the host's send is their consent). Dropped by the
     * checkout page the moment the renter edits dates/times/location, so a
     * mismatched token simply falls through to standard request-to-book.
     */
    quoteToken?: string;
    // Task #5598 — win-back offer token from a declined-booking win-back
    // deep-link. Forwarded so the server attributes the conversion + halts
    // the follow-up cadence.
    winBackToken?: string;
    // Task #5598 ext — availability-probe instant-confirm consent token. When
    // the owner answered "yes, book instantly", the win-back deep-link carries
    // ?probe=<token>; the server validates it (renter/car/dates/type) and forces
    // instant confirmation. Only sent on the legacy POST /bookings path.
    probeToken?: string;
    // Task #6837 — set true when the always-visible clickwrap/MIT-consent
    // notice was rendered above the Pay button (records the renter's Terms +
    // merchant-initiated-charge authorization).
    checkout_terms_accepted?: boolean;
    checkout_occasion_capability?: "required-v1";
    checkout_driver_choice_capability?: "required-v1";
    /** Server-authoritative capture contract from the pre-submit quote. */
    expectedCaptureMethod?: "automatic_async" | "manual";
  },
  // C1 — a per-attempt idempotency key prevents the server from creating
  // duplicate bookings if the client retransmits (network retry, double-tap
  // that slips past the in-flight guard, etc.). The caller generates a
  // fresh crypto.randomUUID() for each intentional attempt; retries for the
  // same attempt re-use the same key so the server deduplicates safely.
  idempotencyKey?: string,
  ): Promise<BookingResult> {
    return authFetch("/bookings", token, {
      method: "POST",
      body: JSON.stringify({ ...data, checkout_phone_verification: "post-booking-v1", acquisitionConsent: acquisitionConsent(), bookingAnalyticsContext: getBookingAnalyticsContext() }),
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
  },

  // Task #4316 — host builds + sends a custom quote from a conversation. No
  // trip is created yet; the server prices it through the canonical
  // calculateQuote path with the host's overrides, enforces the rental-fee
  // floor, posts a `trip_quote` system card into the conversation, and returns
  // the bearer token the renter carries into checkout (`?quote=<token>`).
  createTripQuote(
    token: string,
    data: {
      conversationId: number;
      carId: number;
      rentalType: string;
      dateFrom: string;
      dateTo: string;
      pickupTime?: string;
      dropoffTime?: string;
      hours?: number;
      isDelivery?: boolean;
      deliveryDistance?: number;
      airportCode?: string;
      deliveryAddress?: string;
      pickupLatitude?: number;
      pickupLongitude?: number;
      rentalFeeOverride: number;
      deliveryFeeOverride?: number;
      cancellationPolicy?: string;
      insurancePackageId?: number | null;
      message?: string;
    },
  ): Promise<{ token: string; quoteId: number; expiresAt: string; quote: PriceQuote; minRentalFee: number }> {
    return authFetch("/bookings/quotes", token, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Task #4316 — renter reads a host-built quote at checkout. On-read expiry is
  // enforced server-side, so `status` already reflects an expired quote.
  //
  // Task #8492 — this read goes through `authFetch`, not the bounded
  // `apiFetch` task #8483 fixed. A host custom-quote link
  // (`/checkout?quote=<token>`) is the same failure class as that
  // incident: a stalled upstream here left the renter's read of the
  // host's price silently hanging with no thrown error and no Sentry
  // breadcrumb. Layer the identical internal-watchdog pattern — the
  // caller never passes its own signal into this call, so any
  // `AbortError` observed here is unambiguously OUR timer, not a
  // caller-driven cancellation.
  getTripQuote(token: string, quoteToken: string): Promise<TripQuoteDetail> {
    const controller = new AbortController();
    const startedAt = Date.now();
    const timer = setTimeout(() => controller.abort(), QUOTE_FETCH_TIMEOUT_MS);
    return authFetch<TripQuoteDetail>(`/bookings/quotes/${encodeURIComponent(quoteToken)}`, token, {
      signal: controller.signal,
    })
      .catch((err) => {
        if ((err as { name?: string } | null)?.name === "AbortError") {
          reportQuoteStall({
            route: "/bookings/quotes/:token",
            stage: "custom_quote_read",
            elapsedMs: Date.now() - startedAt,
            budgetMs: QUOTE_FETCH_TIMEOUT_MS,
          });
          throw new QuoteTimeoutError();
        }
        throw err;
      })
      .finally(() => clearTimeout(timer));
  },

  // Host cancels a live quote they sent. Owner-only on the server; flips the
  // quote to `cancelled` and updates the in-thread `trip_quote` card so both
  // parties see it live. After this the renter can no longer pay against it.
  cancelTripQuote(token: string, quoteToken: string): Promise<{ token: string; status: string }> {
    return authFetch(`/bookings/quotes/${encodeURIComponent(quoteToken)}/cancel`, token, {
      method: "POST",
    });
  },

  getInitialRequestFinancialSnapshot(token: string, tripId: number): Promise<{
    snapshot: import("./request-financial-baseline").RequestFinancialSnapshot | null;
  }> {
    return authFetch(`/bookings/${tripId}/initial-request-financial-snapshot`, token);
  },

  getMyTrips(token: string, tab = "upcoming", includeClaimedGuests = false): Promise<TripListItem[]> {
    return authFetch(`/bookings/my-trips?tab=${tab}${includeClaimedGuests ? "&include_claimed_guests=1" : ""}`, token);
  },

  getMyDriving(token: string): Promise<MyDrivingTrip[]> {
    return authFetch("/bookings/my-driving", token);
  },
  getDrivingAssessment(assignmentId: string, token: string): Promise<import("@workspace/copy").AccountDrivingProgress> {
    return authFetch(`/bookings/driving-assignments/${encodeURIComponent(assignmentId)}/assessment`, token);
  },
  continueDrivingChecks(assignmentId: string, action: string, token: string): Promise<import("@workspace/copy").DrivingHandoff> {
    return authFetch(`/bookings/driving-assignments/${encodeURIComponent(assignmentId)}/continue`, token, {
      method: "POST", body: JSON.stringify({ action }),
    });
  },

  getOwnerBookings(token: string, tab = "pending"): Promise<TripListItem[]> {
    return authFetch(`/bookings/owner-bookings?tab=${tab}`, token);
  },

  getTripDetail(token: string, publicId: string, signal?: AbortSignal): Promise<TripData> {
    return authFetch(`/bookings/${publicId}`, token, { signal });
  },

  setPrimaryDriver(
    token: string,
    publicId: string,
    input: { renterIsDriver: boolean; email?: string },
  ): Promise<{ primaryDriver: NonNullable<TripData["primaryDriver"]> }> {
    return authFetch(`/bookings/${encodeURIComponent(publicId)}/primary-driver`, token, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  addMyselfAsAdditionalDriver(token: string, publicId: string): Promise<{ assignmentId: number; idempotent: boolean }> {
    return authFetch(`/bookings/${encodeURIComponent(publicId)}/additional-driver/self`, token, { method: "POST" });
  },

  inviteAdditionalDriver(
    token: string,
    publicId: string,
    input: { email: string },
  ): Promise<{
    assignment: { id: number; role: "additional"; status: string; expiresAt?: string };
    idempotent?: boolean;
  }> {
    return authFetch(`/bookings/${encodeURIComponent(publicId)}/additional-driver`, token, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  revokeAdditionalDriver(
    token: string,
    publicId: string,
    assignmentId: number,
  ): Promise<{ assignment: { id: number; role: "additional"; status: "revoked" } }> {
    return authFetch(
      `/bookings/${encodeURIComponent(publicId)}/additional-driver/${encodeURIComponent(String(assignmentId))}/revoke`,
      token,
      { method: "POST" },
    );
  },

  acceptPrimaryDriverInvitation(
    token: string,
    invitationToken: string,
  ): Promise<{
    assignment: { id: number; status: string };
    subject?: "renter_self_drive" | "host_invited_alternate";
    idempotent?: boolean;
    verification_required?: boolean;
    verification_status?: "ready" | "preparing" | "retryable" | "unavailable" | "complete";
    verification_url?: string | null;
    verification_session_id?: string | null;
    verification_start_error?: string | null;
  }> {
    return authFetch("/bookings/alternate-driver/accept", token, {
      method: "POST",
      body: JSON.stringify({ token: invitationToken }),
    });
  },

  getDriverInvitationAssessment(token: string, invitationToken: string, signal?: AbortSignal): Promise<{
    outcome: "pending" | "accepted" | "approved" | "declined" | "revoked" | "expired" | "booking_unavailable";
    subject: "renter_self_drive" | "host_invited_alternate";
    canDecline: boolean;
    checksSubmitted?: boolean;
    canResumeChecks?: boolean;
  }> {
    return authFetch("/bookings/alternate-driver/assessment", token, {
      method: "GET", signal, headers: { "X-Driver-Invitation-Token": invitationToken },
    });
  },

  getDriverInvitationSummary(token: string, invitationToken: string, signal?: AbortSignal): Promise<{
    car: { name: string | null; year: number | null; imageUrl: string | null; city: string | null; region: string | null } | null;
    startAt: string | null;
    endAt: string | null;
    timesCaptured: boolean;
  }> {
    return authFetch("/bookings/alternate-driver/summary", token, {
      method: "GET", signal, headers: { "X-Driver-Invitation-Token": invitationToken },
    });
  },

  getAlternateDriverInvitationStatus(
    token: string,
    invitationToken: string,
    signal?: AbortSignal,
  ): Promise<{
    subject?: "renter_self_drive" | "host_invited_alternate";
    host_is_driver?: boolean;
    assignment: {
      id: number;
      status: string;
      accepted_at?: string | null;
      confirmed_at?: string | null;
      expires_at?: string | null;
      verification_status?: "ready" | "preparing" | "retryable" | "unavailable" | "complete";
      verification_url?: string | null;
    };
  }> {
    return authFetch(
      `/bookings/alternate-driver/status?token=${encodeURIComponent(invitationToken)}`,
      token,
      { method: "GET", signal },
    );
  },

  declinePrimaryDriverInvitation(
    token: string,
    invitationToken: string,
  ): Promise<{
    ok: true;
    assignment: { id: number; status: "declined" };
    idempotent: boolean;
  }> {
    return authFetch("/bookings/alternate-driver/decline", token, {
      method: "POST",
      body: JSON.stringify({ token: invitationToken }),
    });
  },

  // Task #8210 — host invites an alternate driver for a US chauffeured/event
  // booking. Server enforces owner + US + rental-type + booking-state gates.
  inviteAlternateDriver(
    token: string,
    tripId: number,
    email: string,
  ): Promise<{ assignment: { id: number; status: string; invited_email: string | null; expires_at: string | null }; invitation_sent: boolean }> {
    return authFetch(`/bookings/${tripId}/alternate-driver/invite`, token, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  resendAlternateDriverInvitation(
    token: string,
    tripId: number,
  ): Promise<{ assignment: { id: number; status: string; expires_at: string | null }; invitation_sent: boolean }> {
    return authFetch(`/bookings/${tripId}/alternate-driver/resend`, token, {
      method: "POST",
    });
  },

  revokeAlternateDriver(token: string, tripId: number): Promise<{ success: boolean }> {
    return authFetch(`/bookings/${tripId}/alternate-driver/revoke`, token, {
      method: "POST",
    });
  },

  async getPreviewTrip(carId: number, rentalType?: string): Promise<TripData> {
    const qs = rentalType ? `?rental_type=${encodeURIComponent(rentalType)}` : "";
    const path = `/bookings/preview/${carId}${qs}`;
    const res = await boundedBookingFetch(`${API_BASE}${path}`, undefined, path);
    if (!res.ok) throw new Error(`Preview unavailable (${res.status})`);
    return res.json();
  },

  approveBooking(token: string, tripId: number): Promise<{ success: boolean }> {
    return authFetch(`/bookings/${tripId}/approve`, token, { method: "PUT" });
  },

  // Task #6354 — canonical decline: send the picked reason id + the host's
  // free-text comment as SEPARATE fields. `reason` remains as a legacy
  // fallback (free text only, when no picker reasons could be loaded).
  declineBooking(
    token: string,
    tripId: number,
    opts?: { reasonId?: number | null; details?: string; reason?: string },
  ): Promise<DeclineBookingResponse> {
    return authFetch(`/bookings/${tripId}/decline`, token, {
      method: "PUT",
      body: JSON.stringify({
        reasonId: opts?.reasonId ?? undefined,
        details: opts?.details || undefined,
        reason: opts?.reason || undefined,
      }),
    });
  },

  /**
   * Hydrate the pending post-decline availability prompt. Unlike the
   * immediate decline response, this is a fresh server read so a prompt
   * suppressed on another device disappears after a reload or focus refresh.
   */
  async getAvailabilityFollowUp(token: string, tripId: number): Promise<AvailabilityFollowUp | null> {
    const response = await authFetch<AvailabilityFollowUpResponse>(
      `/bookings/${tripId}/availability-follow-up`,
      token,
    );
    return response.availabilityFollowUp ?? null;
  },

  /**
   * Mark the post-decline availability follow-up as handled. The server
   * endpoint is idempotent, so retrying this request after a transient error
   * cannot create duplicate state.
   */
  suppressAvailabilityFollowUp(token: string, tripId: number): Promise<AvailabilityFollowUpSuppressionResponse> {
    return authFetch(`/bookings/${tripId}/availability-follow-up/suppress`, token, {
      method: "POST",
    });
  },

  createPaymentForApproved(token: string, tripId: number): Promise<{ clientSecret: string; stripeRegion?: string; stripePublishableKey?: string }> {
    return authFetch(`/bookings/${tripId}/pay`, token, { method: "POST" });
  },

  // Build-a-trip claim flow. The landing page first introspects the token
  // (GET) to render a friendly message for expired / already-claimed /
  // cancelled invites, then POSTs to consume it and route the renter into
  // the existing pay flow. Both endpoints surface a structured `code` on
  // failure so the landing page can branch on token state.
  async getClaimInvite(token: string, claimToken: string): Promise<ClaimInviteResponse> {
    return claimRequest(claimToken, token, "GET");
  },
  async claimInvite(token: string, claimToken: string): Promise<ClaimInviteResponse> {
    return claimRequest(claimToken, token, "POST");
  },

  // Pay-later (task #394). Renter retry flow for failed off-session charges.
  retryPayLater(token: string, tripId: number): Promise<{ clientSecret: string; stripeRegion?: string; stripePublishableKey?: string }> {
    return authFetch(`/bookings/${tripId}/pay-later/retry`, token, { method: "POST" });
  },

  // Task #731: renter self-serve retry for a failed security-deposit pre-auth.
  // Task #741: response also carries the auto-cancel cutoff (`retryDeadline`)
  // so the retry page can render a countdown without recomputing it.
  retryDeposit(token: string, tripId: number): Promise<{ clientSecret: string; stripeRegion?: string; stripePublishableKey?: string; retryDeadline?: string | null; attemptsRemaining?: number }> {
    return authFetch(`/bookings/${tripId}/deposit/retry`, token, { method: "POST" });
  },

  setWorkTrip(token: string, tripId: number, workTrip: boolean): Promise<{ success: boolean; work_trip: boolean }> {
    return authFetch(`/bookings/${tripId}/work-trip`, token, {
      method: "PATCH",
      body: JSON.stringify({ work_trip: workTrip }),
    });
  },

  setOccasion(token: string, publicId: string, occasion: string | null): Promise<{ success: boolean; occasion: string | null }> {
    return authFetch(`/bookings/${publicId}/occasion`, token, {
      method: "PATCH",
      body: JSON.stringify({ occasion }),
    });
  },

  cancelBooking(token: string, tripId: number, reasonId?: number, details?: string): Promise<CancelBookingResult> {
    return authFetch(`/bookings/${tripId}/cancel`, token, {
      method: "PUT",
      body: JSON.stringify({ reasonId, details }),
    });
  },

  withdrawBooking(token: string, tripId: number, reasonId?: number, details?: string): Promise<CancelBookingResult> {
    return authFetch(`/bookings/${tripId}/cancel`, token, {
      method: "PUT",
      body: JSON.stringify({ renterWithdraw: true, ...(reasonId != null ? { reasonId } : {}), details }),
    });
  },

  // Task #3396 — proactively release the renter's just-created PENDING
  // booking + its calendar holds when the inline checkout payment fails
  // (3DS declined/abandoned, card error). Fire-and-forget cleanup so a retry
  // with a working card never dead-ends on the renter's own orphan hold.
  // Accepts the trip's public_id (UUID) or numeric id; idempotent server-side.
  abandonPayment(token: string, tripIdOrPublicId: string | number): Promise<{ success: boolean; cancelled: boolean }> {
    return authFetch(`/bookings/${tripIdOrPublicId}/abandon-payment`, token, {
      method: "POST",
    });
  },

  // Task #4736 — after the renter confirms the card (3DS / authorization) for a
  // NEW-card booking REQUEST, ping the server so the renter↔host fan-out
  // (conversation + system card + host alert) fires immediately instead of
  // waiting on the Stripe webhook (which, if missed, lost the chat forever when
  // the host declined first). Accepts the trip's public_id (UUID) or numeric
  // id. The endpoint is fully self-gating + verifies the Stripe hold, so this
  // is harmless for instant bookings / saved-card / abandoned 3DS — fire it
  // best-effort and ignore failures.
  confirmRequestHold(
    token: string,
    tripIdOrPublicId: string | number,
  ): Promise<{ ok: boolean; notified: boolean; reason: string }> {
    return authFetch(`/bookings/${tripIdOrPublicId}/confirm-request-hold`, token, {
      method: "POST",
    });
  },

  finaliseNearPickup(token: string, tripIdOrPublicId: string | number): Promise<{ outcome: string }> {
    return authFetch(`/bookings/${tripIdOrPublicId}/finalise-near-pickup`, token, { method: "POST" });
  },

  getCancellationPolicySummary(token: string, publicId: string): Promise<CancellationPolicySummary> {
    return authFetch(`/bookings/${publicId}/cancellation-policy`, token);
  },

  getCancellationReasons(audience?: "host" | "renter" | "withdrawal", token?: string): Promise<CancellationReason[]> {
    // Withdrawal reasons are a renter audience with a distinct server-side
    // flow discriminator. Keep the customer-facing call expressive without
    // leaking the endpoint's two query parameters into page components.
    const qs = audience === "withdrawal"
      ? "?audience=renter&flow=withdrawal"
      : audience ? `?audience=${audience}` : "";
    return apiFetch(`/bookings/cancellation-reasons${qs}`, token);
  },

  getDeclineReasons(token?: string): Promise<CancellationReason[]> {
    return apiFetch("/bookings/cancellation-reasons?audience=decline", token);
  },

  createDepositIntent(token: string, tripId: number): Promise<{ clientSecret: string | null; noDeposit?: boolean }> {
    return authFetch("/bookings/stripe/create-deposit", token, {
      method: "POST",
      body: JSON.stringify({ tripId }),
    });
  },

  createStripeConnectAccount(token: string): Promise<{ url: string }> {
    return authFetch("/bookings/stripe/connect/create-account", token, { method: "POST" });
  },

  // Task #1092 Phase B — host-facing payout settings + payout-link list.
  getMyPayoutPolicy(token: string): Promise<{
    decision: {
      payoutMethod: "connect" | "payout_link" | "manual" | "awaiting_setup";
      tier: number;
      source: string;
      reason?: string;
      hostOverrideApplied?: boolean;
    };
    preferredPayoutMethod: "connect" | "payout_link" | "manual" | null;
  }> {
    // Keep a token-refresh retry bound to the host who started this read.
    // The hook discards late results, but a retry must not silently change host.
    return authFetch("/users/me/payout-policy", token, undefined, getImpersonationHeaders());
  },

  async updatePreferredPayoutMethod(
    token: string,
    preferredPayoutMethod: "connect" | "payout_link" | "manual" | null,
  ): Promise<{ ok: true; preferredPayoutMethod: string | null }> {
    // Sensitive — server gates with requireStepUp("change_payout_method").
    const { withStepUp } = await import("./step-up");
    const res = await withStepUp(() =>
      boundedBookingFetch(`${API_BASE}/users/me/preferred-payout-method`, {
        method: "PATCH",
        headers: authHeaders(token),
        body: JSON.stringify({ preferredPayoutMethod }),
      }, "/users/me/preferred-payout-method"),
    );
    if (!res.ok) {
      let body: Record<string, unknown> | undefined;
      try { body = await res.json(); } catch { /* ignore */ }
      throw new ApiError(
        (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
        res.status,
        body,
      );
    }
    return res.json();
  },

  getMyPayoutLinks(token: string): Promise<{
    items: Array<{
      id: number;
      trip_id: string;
      status: "pending" | "sent" | "completed" | "failed" | "expired" | "cancelled";
      amount: number;
      currency: string;
      url: string;
      expires_at: string;
      sent_at: string | null;
      completed_at: string | null;
      failed_at: string | null;
      created_at: string;
      retry_count: number;
      last_error: string | null;
    }>;
  }> {
    return authFetch("/users/me/payout-links", token);
  },

  // Task #6212 — mint a FRESH Stripe onboarding link for an open payout link at
  // click-time. Stripe account links are single-use and short-lived, so the
  // page never redirects to a stored url; it calls this then navigates to the
  // returned `url`. Returns 404 (foreign/absent), 409 (settled/closed) or 502
  // (mint failed) which the caller surfaces as a toast.
  getPayoutOnboardingLink(payoutLinkId: number, token: string): Promise<{ url: string }> {
    return authFetch(`/users/me/payout-links/${encodeURIComponent(String(payoutLinkId))}/onboarding-link`, token);
  },

  // Task #7726 — Trigger immediate release of held/escalated payout holds
  // when the host lands back on the payouts page after Stripe Connect
  // onboarding. Called with the `?onboarded=1` query param on mount.
  // Task #7731 — The endpoint now returns `connectStatus` (same shape as
  // getStripeConnectStatus, minus balance/bankInfo) so the frontend can
  // apply the fresh Stripe state immediately without waiting for reload().
  notifyConnectOnboardingComplete(token: string): Promise<{
    status: "no_account" | "not_ready" | "released";
    count?: number;
    /** Fresh connect status returned from the onboarding-complete endpoint,
     *  built from the Stripe accounts.retrieve call the server already makes
     *  for the readiness check. Apply immediately to avoid a stale-banner
     *  flash while reload() re-fetches /bookings/stripe/connect/status.
     *  Shape mirrors getStripeConnectStatus; balance/bankInfo are omitted. */
    connectStatus?: {
      connected: boolean;
      accountId: string | null;
      chargesEnabled?: boolean;
      payoutsEnabled?: boolean;
      detailsSubmitted?: boolean;
      needsOnboarding?: boolean;
      disabledReason?: string | null;
      currentlyDue?: string[];
      balance?: null;
      bankInfo?: null;
    } | null;
  }> {
    return authFetch("/users/me/connect/onboarding-complete", token, { method: "POST" });
  },

  getStripeConnectStatus(token: string): Promise<{
    connected: boolean;
    accountId: string | null;
    chargesEnabled?: boolean;
    payoutsEnabled?: boolean;
    detailsSubmitted?: boolean;
    /** True when the account record exists but onboarding is incomplete
     *  (charges/payouts disabled, details not submitted, or
     *  requirements past due). UI gates that show "Connect with Stripe"
     *  CTAs should treat `needsOnboarding === true` the same as
     *  `connected === false`, otherwise hosts who abandoned the hosted
     *  onboarding flow get stuck with no way back in. */
    needsOnboarding?: boolean;
    disabledReason?: string | null;
    /** Task #7311 — outstanding Stripe requirements so the UI can show
     *  targeted copy (e.g. "Accept Terms of Service") instead of the
     *  generic "finish onboarding" prompt. */
    currentlyDue?: string[];
    balance?: {
      available: number;
      pending: number;
      currency: string;
    } | null;
    bankInfo?: {
      bankName: string | null;
      last4: string;
      currency: string;
    } | null;
  }> {
    return authFetch("/bookings/stripe/connect/status", token);
  },

  getOwnerEarnings(token: string): Promise<EarningsData> {
    return authFetch("/bookings/owner-earnings", token);
  },

  // Task #1093 — host flags a discrepancy on a manual payout that hit
  // their bank for the wrong amount / didn't arrive. The recording admin
  // is notified in-app + by email.
  flagManualPayoutDiscrepancy(
    token: string,
    manualPayoutId: number,
    reason: string,
  ): Promise<{ ok: true; manual_payout_id: number }> {
    return authFetch(`/me/payouts/manual/${manualPayoutId}/flag-discrepancy`, token, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  // Task #1093 (review fix) — host-side per-trip lookup so the host's
  // booking detail page can render the manual payout the admin
  // recorded (with discrepancy flag + bank reference) inline with the
  // payment breakdown, no need to leave the page.
  fetchTripManualPayouts(
    token: string,
    tripId: number,
  ): Promise<{ items: TripManualPayout[] }> {
    return authFetch(`/me/payouts/manual/by-trip/${tripId}`, token);
  },

  getOwnerEarningsExport(token: string): Promise<{ transactions: Array<{
    date: string;
    start_date: string | null;
    end_date: string | null;
    car: string;
    trip_reference: string;
    amount: number;
    rental_fee: number;
    service_fee: number;
    payment_due: number;
    type: string;
    status: string;
    currency: string;
  }> }> {
    return authFetch("/bookings/owner-earnings/export", token);
  },

  // Task #1282 (SEC-001) — XLSX export is generated server-side via
  // exceljs so the browser bundle no longer needs the vulnerable `xlsx`
  // package. Returns a binary Excel blob streamed by the API.
  async getOwnerEarningsExportXlsx(
    token: string,
    range: "30d" | "3m" | "ytd" | "all" = "all",
  ): Promise<Blob> {
    const res = await boundedBookingFetch(
      `${API_BASE}/bookings/owner-earnings/export?format=xlsx&range=${encodeURIComponent(range)}`,
      { headers: { Authorization: `Bearer ${token}` } },
      "/bookings/owner-earnings/export",
    );
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        const body = await res.json();
        if (body && typeof body.error === "string") message = body.error;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    return res.blob();
  },

  // Task #2965 — the legacy `submitReview` wrapper around
  // `POST /bookings/review` was deleted. The unified renter+host
  // review flow now goes through `usersApi.createReview` →
  // `POST /users/me/reviews`. Import from `@/lib/users-api` instead.

  getPaymentMethods(token: string, region?: StripeRegion): Promise<{ paymentMethods: SavedPaymentMethod[]; region?: StripeRegion }> {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return authFetch(`/bookings/payment-methods${qs}`, token);
  },

  async createSetupIntent(token: string, region?: StripeRegion): Promise<{ clientSecret: string; stripePublishableKey?: string; region?: StripeRegion }> {
    // Sensitive — gated by requireStepUp("add_payment_method").
    const { withStepUp } = await import("./step-up");
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    const res = await withStepUp(() =>
      boundedBookingFetch(`${API_BASE}/bookings/payment-methods/setup${qs}`, {
        method: "POST",
        headers: authHeaders(token),
      }, "/bookings/payment-methods/setup"),
    );
    if (!res.ok) {
      let body: Record<string, unknown> | undefined;
      try { body = await res.json(); } catch { /* ignore */ }
      throw new ApiError(
        (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
        res.status,
        body,
      );
    }
    return res.json();
  },

  async deletePaymentMethod(token: string, pmId: string, region?: StripeRegion): Promise<{ success: boolean }> {
    // Sensitive — gated by requireStepUp("remove_payment_method").
    const { withStepUp } = await import("./step-up");
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    const res = await withStepUp(() =>
      boundedBookingFetch(`${API_BASE}/bookings/payment-methods/${pmId}${qs}`, {
        method: "DELETE",
        headers: authHeaders(token),
      }, "/bookings/payment-methods/:id"),
    );
    if (!res.ok) {
      let body: Record<string, unknown> | undefined;
      try { body = await res.json(); } catch { /* ignore */ }
      throw new ApiError(
        (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
        res.status,
        body,
      );
    }
    return res.json();
  },

  async setDefaultPaymentMethod(token: string, pmId: string, region: StripeRegion): Promise<{ success: boolean; defaultPaymentMethodId: string }> {
    // Sensitive — gated by requireStepUp("set_default_payment_method").
    const { withStepUp } = await import("./step-up");
    const res = await withStepUp(() =>
      boundedBookingFetch(`${API_BASE}/bookings/payment-methods/${pmId}/default?region=${encodeURIComponent(region)}`, {
        method: "PUT",
        headers: authHeaders(token),
      }, "/bookings/payment-methods/:id/default"),
    );
    if (!res.ok) {
      let body: Record<string, unknown> | undefined;
      try { body = await res.json(); } catch { /* ignore */ }
      throw new ApiError(
        (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
        res.status,
        body,
      );
    }
    return res.json();
  },

  async getInspectionImages(token: string, tripId: number): Promise<{ images: InspectionImage[]; loadError?: boolean }> {
    // Task #1222 — never throw on a failed inspection-images fetch. The
    // booking detail page (renter, host, admin) renders the rest of the
    // booking even if this section can't load, and an exception here
    // would otherwise bubble up to Sentry as `Failed to fetch inspection
    // images` (DRIVESHARE_V2-2Y). Treat 5xx as "no inspections" with an
    // inline notice; rethrow only for client errors that we want callers
    // to see (e.g. 401 logged-out flows).
    try {
      return await authFetch<{ images: InspectionImage[] }>(`/bookings/${tripId}/inspections`, token);
    } catch (err) {
      if (err instanceof BookingApiError && err.status >= 500) {
        return { images: [], loadError: true };
      }
      throw err;
    }
  },

  requestInspectionUploadUrl(token: string, tripId: number, contentType: string): Promise<{ uploadURL: string; objectPath: string; uploadToken: string }> {
    return authFetch(`/bookings/${tripId}/inspections/request-upload-url`, token, {
      method: "POST",
      body: JSON.stringify({ contentType }),
    });
  },

  saveInspectionImage(token: string, tripId: number, data: { objectPath: string; inspectionType: "pre_trip" | "post_trip"; note?: string; uploadToken: string }): Promise<InspectionImage> {
    return authFetch(`/bookings/${tripId}/inspections`, token, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  createTripAdjustment(token: string, tripId: number, data: {
    date_from: string;
    date_to: string;
    rental_fee?: number;
    delivery_fee?: number;
    service_fee?: number;
    insurance_fee?: number;
    deposit?: number;
    message?: string;
    counter_of?: number;
    // Task #975: time + location extensions. Null time values mean no
    // change; for nullable delivery-location values, undefined inherits and
    // null explicitly clears the field.
    pickup_at?: string | null;
    dropoff_at?: string | null;
    is_delivery?: boolean | null;
    delivery_distance?: number | null;
    airport_code?: string | null;
    delivery_address?: string | null;
    pickup_latitude?: number | null;
    pickup_longitude?: number | null;
    pickup_proof?: string;
  }): Promise<TripAdjustment> {
    return authFetch(`/bookings/${tripId}/trip-adjustment`, token, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  getTripAdjustments(token: string, tripId: number): Promise<TripAdjustment[]> {
    return authFetch(`/bookings/${tripId}/trip-adjustments`, token);
  },

  respondToAdjustment(
    token: string,
    tripId: number,
    adjustmentId: number,
    action: "accept" | "decline",
  ): Promise<{
    success: boolean;
    action?: string;
    adjustmentId?: number;
    /** When true, the off-session charge needs SCA / 3DS authentication before finalize. */
    requiresAuthentication?: boolean;
    paymentIntentId?: string;
    clientSecret?: string;
    stripeRegion?: StripeRegion;
    stripePublishableKey?: string;
    amount?: number;
    currency?: string;
    /**
     * Task #2920 — present (and `true`) when the actor was the host
     * (or admin force-accept) and the renter's card needs SCA. The
     * server has already parked the PI and notified the renter via
     * system message + push + email; the host's UI should flip to
     * "Accepted — waiting for renter to authorize" and MUST NOT
     * attempt `stripe.confirmCardPayment` (the renter's card cannot
     * authenticate from the host's browser).
     */
    acceptedPendingRenterConfirmation?: boolean;
  }> {
    return authFetch(`/bookings/${tripId}/trip-adjustments/${adjustmentId}/respond`, token, {
      method: "POST",
      body: JSON.stringify({ action }),
    });
  },

  /**
   * Task #2405: lets the proposer withdraw their own still-pending
   * trip-adjustment. The booking's underlying terms (dates, location,
   * price) are not modified — cancelling a proposal only releases the
   * "Request a change" entry point so a fresh proposal can be made.
   */
  cancelTripAdjustment(token: string, tripId: number, adjustmentId: number): Promise<{
    success: boolean;
    adjustmentId: number;
    cancelled_at: string;
  }> {
    return authFetch(`/bookings/${tripId}/trip-adjustments/${adjustmentId}/cancel`, token, {
      method: "POST",
    });
  },

  getAdjustmentPaymentIntent(token: string, tripId: number, adjustmentId: number): Promise<{
    paymentIntentId: string;
    clientSecret: string;
    status: string;
    stripeRegion: StripeRegion;
    stripePublishableKey: string;
    amount: number;
    currency: string;
  }> {
    return authFetch(`/bookings/${tripId}/trip-adjustments/${adjustmentId}/payment-intent`, token);
  },

  finalizeAdjustmentPayment(token: string, tripId: number, adjustmentId: number): Promise<{
    success: boolean;
    adjustmentId: number;
    paymentIntentId: string;
    status: string;
  }> {
    return authFetch(`/bookings/${tripId}/trip-adjustments/${adjustmentId}/finalize-payment`, token, {
      method: "POST",
    });
  },

  createCheckoutIntent(token: string, data: { carId: number; dateFrom: string; dateTo: string; rentalType: string; hours?: number; insurancePackageId?: number | null; occasion?: string | null }): Promise<{ id: number; upserted: boolean }> {
    return authFetch("/checkout-intents", token, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateCheckoutIntentStep(token: string, intentId: number, checkoutStepReached: string, occasion?: string | null): Promise<{ success: boolean }> {
    return authFetch(`/checkout-intents/${intentId}`, token, {
      method: "PATCH",
      body: JSON.stringify({ checkoutStepReached, occasion }),
    });
  },

  async uploadInspectionPhoto(token: string, tripId: number, file: Blob, inspectionType: "pre_trip" | "post_trip", note?: string): Promise<InspectionImage> {
    const doUploadAndBind = async (): Promise<InspectionImage> => {
      const mint = await this.requestInspectionUploadUrl(token, tripId, "image/jpeg");
      const putRes = await boundedBookingFetch(mint.uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "image/jpeg" },
      }, "/bookings/:id/inspections/upload");
      if (!putRes.ok) {
        throw new Error("Failed to upload inspection photo to storage");
      }
      return this.saveInspectionImage(token, tripId, {
        objectPath: mint.objectPath,
        inspectionType,
        note,
        uploadToken: mint.uploadToken,
      });
    };

    try {
      return await doUploadAndBind();
    } catch (err: unknown) {
      // 403 = upload token expired between mint and bind — re-mint once and
      // retry the full upload so the new token covers the new objectPath.
      if (err instanceof BookingApiError && err.status === 403) {
        return doUploadAndBind();
      }
      throw err;
    }
  },
};

/** Task #8440 — snapshot of the Better quote frozen onto a trip the moment
 *  the host enables cover. Mirrors the API's `HostInsurancePolicySnapshot`. */
export interface HostInsurancePolicySnapshot {
  provider: "better";
  tier: "Better";
  package_id: number;
  package_name: string;
  rental_type: "chauffeured" | "event";
  country_code: "US";
  state_code: string;
  insured_value: number;
  rental_days: number;
  rental_hours: number;
  daily_rate: number;
  final_cost: number;
  liability_cost: number;
  physical_cost: number;
  cf_total_cost: number;
  quoted_at: string;
}

export class BookingApiTimeoutError extends Error {
  readonly code = "BOOKING_API_TIMEOUT";
  readonly status = 408;
  readonly classification: BookingApiTimeoutClassification;

  constructor(classification: BookingApiTimeoutClassification = "booking_service_stall") {
    super("The booking service is taking longer than expected. Please try again.");
    this.name = "BookingApiTimeoutError";
    this.classification = classification;
  }
}

export type BookingApiTimeoutClassification =
  | "booking_service_stall"
  | "client_network_outage";

function classifyBookingApiTimeout(path: string): BookingApiTimeoutClassification {
  return getRecentNetworkFailureSummary(path).unrelatedRoutes.length >= 2
    ? "client_network_outage"
    : "booking_service_stall";
}

function reportBookingApiStall(
  path: string,
  classification: BookingApiTimeoutClassification = classifyBookingApiTimeout(path),
): void {
  const networkFailures = getRecentNetworkFailureSummary(path);
  const context = {
    route: path.split("?")[0],
    budgetMs: BOOKING_API_FETCH_TIMEOUT_MS,
    classification,
    clientNetworkOutage: classification === "client_network_outage",
    unrelatedFailuresWithoutHttpStatus: networkFailures.unrelatedRequestCount > 0,
    unrelatedNetworkFailureCount: networkFailures.unrelatedRequestCount,
    unrelatedNetworkFailureRouteCount: networkFailures.unrelatedRoutes.length,
    unrelatedNetworkFailureRoutes: networkFailures.unrelatedRoutes,
  };
  try {
    Sentry.addBreadcrumb({
      category: "booking-api-stall",
      level: "warning",
      message: "BOOKING_API_TIMEOUT",
      data: context,
    });
    Sentry.captureMessage(
      classification === "client_network_outage"
        ? "[booking-api-stall] client BOOKING_API_TIMEOUT hit during client network outage"
        : "[booking-api-stall] client BOOKING_API_TIMEOUT hit",
      {
        level: "warning",
        tags: {
          code: "BOOKING_API_TIMEOUT",
          classification,
        },
        extra: context,
      },
    );
  } catch {
    // Reporting must never mask the timeout callers need to recover from.
  }
}

async function boundedBookingFetch(
  input: string,
  init?: RequestInit,
  routeForReport: string = input,
): Promise<Response> {
  const internalController = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    internalController.abort();
  }, BOOKING_API_FETCH_TIMEOUT_MS);
  const onCallerAbort = () => internalController.abort();
  init?.signal?.addEventListener("abort", onCallerAbort);
  if (init?.signal?.aborted) onCallerAbort();
  try {
    return await fetch(input, { ...init, signal: internalController.signal });
  } catch (err) {
    if (timedOut && (err as { name?: string } | null)?.name === "AbortError") {
      const classification = classifyBookingApiTimeout(routeForReport);
      reportBookingApiStall(routeForReport, classification);
      throw new BookingApiTimeoutError(classification);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    init?.signal?.removeEventListener("abort", onCallerAbort);
  }
}

/** Minimal canonical pricing model needed by the change-proposal UI. */
export interface TripAdjustmentQuote {
  financialAudience?: "host";
  hostDisplayTotal?: number;
  tax?: PriceQuote["tax"];
  pickupProof?: string;
  rentalFee: number;
  baseRentalFee: number;
  serviceFee: number;
  insuranceFee: number;
  insurancePackageName: string | null;
  insurancePolicy?: InsurancePolicySnapshot | null;
  deliveryFee: number;
  deposit: number;
  paymentDue?: number;
  totalIncludingTax?: number;
  ownerEarn?: number;
  ownerShare?: number;
  rentalType: string;
  numDays: number;
  hoursRequested?: number;
  eventHoursRequested?: number;
  couponDiscount?: number;
  proOwnerBookingDiscount?: number;
  proOwnerTier?: string | null;
  appliedCoupon?: PriceQuote["appliedCoupon"];
  pricingFloors?: PriceQuote["pricingFloors"];
  hostInsuranceEnabled?: boolean;
  hostInsuranceFee?: number;
  hostInsurancePolicy?: HostInsurancePolicySnapshot | null;
  ownerEarnBeforeHostInsurance?: number;
  policyRefundPreview?: number | null;
}
